import type { LevelDef } from '../core/game';
import { HUB_LEVELS } from './hubLevels';
import { isOriginLevelId } from './originEvent';

export const LEVELS: LevelDef[] = [
  {
    id: 1,
    episode: 'I',
    name: 'The Picnic Menace',
    subtitle: 'Picnic Clearing',
    background: 'bg-forest',
    path: [
      { x: 0, y: 130 },
      { x: 230, y: 130 },
      { x: 230, y: 410 },
      { x: 470, y: 410 },
      { x: 470, y: 150 },
      { x: 720, y: 150 },
      { x: 720, y: 470 },
      { x: 1040, y: 470 },
    ],
    // Inside-corner plots: one per turn (on the concave side, covering both
    // legs), plus buddy plots stepped 96px along a leg for slow+damage combos.
    // Every pair of plots is ≥96px apart (tiles are 64px — they never touch).
    plots: [
      { id: 0, pos: { x: 181, y: 179 } },
      { id: 1, pos: { x: 279, y: 361 } },
      { id: 2, pos: { x: 421, y: 361 } },
      { id: 3, pos: { x: 519, y: 199 } },
      { id: 4, pos: { x: 671, y: 199 } },
      { id: 5, pos: { x: 769, y: 421 } },
      { id: 6, pos: { x: 85, y: 179 } },
      { id: 7, pos: { x: 279, y: 265 } },
    ],
    startCash: 470, // tight opening budget — kill bounties fund the build
    startLives: 5,
    coinReward: 10, // persistent coins for winning Episode I
    waves: [
      { groups: [{ enemyType: 'grunt', count: 4, interval: 1.5, startDelay: 0 }] },
      { groups: [{ enemyType: 'grunt', count: 5, interval: 1.2, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.55, startDelay: 0 },
          { enemyType: 'grunt', count: 3, interval: 1.7, startDelay: 6 },
        ],
      },
      {
        groups: [
          { enemyType: 'grunt', count: 7, interval: 1.0, startDelay: 0 },
          { enemyType: 'fry', count: 6, interval: 0.6, startDelay: 5 },
        ],
      },
    ],
  },
  {
    id: 2,
    episode: 'II',
    name: 'Attack of the Crumbs',
    subtitle: 'Crumb Trail',
    background: 'bg-crumb',
    path: [
      { x: 0, y: 120 },
      { x: 210, y: 120 },
      { x: 210, y: 360 },
      { x: 420, y: 360 },
      { x: 420, y: 140 },
      { x: 640, y: 140 },
      { x: 640, y: 420 },
      { x: 860, y: 420 },
      { x: 860, y: 180 },
      { x: 1040, y: 180 },
    ],
    // Inside-corner plots (see Level 1 note).
    plots: [
      { id: 0, pos: { x: 161, y: 169 } },
      { id: 1, pos: { x: 259, y: 311 } },
      { id: 2, pos: { x: 371, y: 311 } },
      { id: 3, pos: { x: 469, y: 189 } },
      { id: 4, pos: { x: 591, y: 189 } },
      { id: 5, pos: { x: 689, y: 371 } },
      { id: 6, pos: { x: 811, y: 371 } },
      { id: 7, pos: { x: 909, y: 229 } },
      { id: 8, pos: { x: 65, y: 169 } },
      { id: 9, pos: { x: 259, y: 215 } },
    ],
    startCash: 550, // tight opening budget — kill bounties fund the build
    startLives: 5,
    coinReward: 15, // persistent coins for winning Episode II
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.2, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'grunt', count: 7, interval: 1.0, startDelay: 0 },
          { enemyType: 'fry', count: 5, interval: 0.5, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'dino', count: 5, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 0 },
          { enemyType: 'dino', count: 4, interval: 0.8, startDelay: 5 },
        ],
      },
      {
        groups: [
          { enemyType: 'grunt', count: 7, interval: 0.8, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 4 },
          { enemyType: 'fry', count: 4, interval: 0.4, startDelay: 8 },
        ],
      },
    ],
  },
  {
    id: 3,
    episode: 'III',
    name: 'Revenge of the Sauce',
    subtitle: 'Condiment Canyon',
    background: 'bg-canyon',
    path: [
      { x: 0, y: 440 },
      { x: 200, y: 440 },
      { x: 200, y: 180 },
      { x: 400, y: 180 },
      { x: 400, y: 520 },
      { x: 600, y: 520 },
      { x: 600, y: 160 },
      { x: 800, y: 160 },
      { x: 800, y: 480 },
      { x: 1040, y: 480 },
    ],
    // Inside-corner plots (see Level 1 note).
    plots: [
      { id: 0, pos: { x: 151, y: 391 } },
      { id: 1, pos: { x: 249, y: 229 } },
      { id: 2, pos: { x: 351, y: 229 } },
      { id: 3, pos: { x: 449, y: 471 } },
      { id: 4, pos: { x: 551, y: 471 } },
      { id: 5, pos: { x: 649, y: 209 } },
      { id: 6, pos: { x: 751, y: 209 } },
      { id: 7, pos: { x: 849, y: 431 } },
      { id: 8, pos: { x: 55, y: 391 } },
      { id: 9, pos: { x: 249, y: 325 } },
      { id: 10, pos: { x: 449, y: 375 } },
      { id: 11, pos: { x: 649, y: 305 } },
    ],
    startCash: 625, // tight opening budget — kill bounties fund the build
    startLives: 5,
    coinReward: 15, // persistent coins for winning Episode III
    waves: [
      { groups: [{ enemyType: 'grunt', count: 8, interval: 1.0, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'grunt', count: 6, interval: 1.0, startDelay: 0 },
          { enemyType: 'fry', count: 6, interval: 0.5, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'dino', count: 6, interval: 0.8, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.5, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 1.0, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 0 },
          { enemyType: 'dino', count: 4, interval: 0.7, startDelay: 3 },
          { enemyType: 'hotdog', count: 2, interval: 2.0, startDelay: 8 },
        ],
      },
    ],
  },
  {
    id: 4,
    episode: 'IV',
    name: 'The Grease Strikes Back',
    subtitle: 'Grease Gauntlet',
    background: 'bg-grease',
    path: [
      { x: 0, y: 300 },
      { x: 180, y: 300 },
      { x: 180, y: 560 },
      { x: 360, y: 560 },
      { x: 360, y: 180 },
      { x: 560, y: 180 },
      { x: 560, y: 520 },
      { x: 760, y: 520 },
      { x: 760, y: 200 },
      { x: 940, y: 200 },
      { x: 940, y: 470 },
      { x: 1040, y: 470 },
    ],
    // Inside-corner plots (see Level 1 note).
    plots: [
      { id: 0, pos: { x: 131, y: 349 } },
      { id: 1, pos: { x: 229, y: 511 } },
      { id: 2, pos: { x: 409, y: 229 } },
      { id: 3, pos: { x: 511, y: 229 } },
      { id: 4, pos: { x: 609, y: 471 } },
      { id: 5, pos: { x: 711, y: 471 } },
      { id: 6, pos: { x: 809, y: 249 } },
      { id: 7, pos: { x: 989, y: 421 } },
      { id: 8, pos: { x: 229, y: 415 } },
      { id: 9, pos: { x: 409, y: 325 } },
      { id: 10, pos: { x: 609, y: 375 } },
      { id: 11, pos: { x: 809, y: 345 } },
      { id: 12, pos: { x: 989, y: 325 } },
      { id: 13, pos: { x: 131, y: 445 } },
    ],
    startCash: 705, // tight opening budget — kill bounties fund the build
    startLives: 5,
    coinReward: 20, // persistent coins for winning Episode IV
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.0, startDelay: 0 }] },
      { groups: [{ enemyType: 'dino', count: 6, interval: 0.7, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'grunt', count: 5, interval: 1.0, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'dino', count: 8, interval: 0.6, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 2 },
          { enemyType: 'dino', count: 4, interval: 0.7, startDelay: 6 },
        ],
      },
      {
        groups: [
          { enemyType: 'grunt', count: 6, interval: 0.8, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.6, startDelay: 3 },
          { enemyType: 'hotdog', count: 2, interval: 2.0, startDelay: 8 },
        ],
      },
    ],
  },
  {
    id: 5,
    episode: 'V',
    name: 'Return of the Golden Nugget',
    subtitle: 'The Last Buffet',
    background: 'bg-buffet',
    path: [
      { x: 0, y: 360 },
      { x: 160, y: 360 },
      { x: 160, y: 120 },
      { x: 340, y: 120 },
      { x: 340, y: 500 },
      { x: 520, y: 500 },
      { x: 520, y: 140 },
      { x: 700, y: 140 },
      { x: 700, y: 520 },
      { x: 880, y: 520 },
      { x: 880, y: 220 },
      { x: 1040, y: 220 },
    ],
    // Inside-corner plots (see Level 1 note).
    plots: [
      { id: 0, pos: { x: 111, y: 311 } },
      { id: 1, pos: { x: 209, y: 169 } },
      { id: 2, pos: { x: 389, y: 451 } },
      { id: 3, pos: { x: 569, y: 189 } },
      { id: 4, pos: { x: 749, y: 471 } },
      { id: 5, pos: { x: 929, y: 269 } },
      { id: 6, pos: { x: 209, y: 265 } },
      { id: 7, pos: { x: 389, y: 355 } },
      { id: 8, pos: { x: 569, y: 285 } },
      { id: 9, pos: { x: 749, y: 375 } },
      { id: 10, pos: { x: 929, y: 365 } },
      { id: 11, pos: { x: 111, y: 215 } },
      { id: 12, pos: { x: 1025, y: 269 } },
      { id: 13, pos: { x: 209, y: 361 } },
      { id: 14, pos: { x: 389, y: 259 } },
      { id: 15, pos: { x: 279, y: 451 } },
    ],
    startCash: 2000, // boss-episode war chest — pairs with the 6000 HP Nugget
    startLives: 5,
    coinReward: 50, // persistent coins for winning Episode V
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.0, startDelay: 0 }] },
      { groups: [{ enemyType: 'dino', count: 6, interval: 0.7, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'grunt', count: 5, interval: 1.0, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'dino', count: 8, interval: 0.6, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'grunt', count: 6, interval: 0.8, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.6, startDelay: 3 },
          { enemyType: 'hotdog', count: 2, interval: 2.0, startDelay: 8 },
        ],
      },
      { groups: [{ enemyType: 'boss', count: 1, interval: 1, startDelay: 0 }] },
    ],
  },
  // ── Chapter 2 ──────────────────────────────────────────────────────────────
  {
    id: 6,
    chapter: 2,
    episode: 'I',
    name: 'Fluffy Peak',
    subtitle: 'Snowbound Pass',
    background: 'bg-mountains',
    path: [
      { x: 0, y: 160 },
      { x: 180, y: 160 },
      { x: 180, y: 440 },
      { x: 360, y: 440 },
      { x: 360, y: 160 },
      { x: 540, y: 160 },
      { x: 540, y: 480 },
      { x: 720, y: 480 },
      { x: 720, y: 180 },
      { x: 900, y: 180 },
      { x: 900, y: 500 },
      { x: 1040, y: 500 },
    ],
    // Inside-corner plots (see Level 1 note).
    plots: [
      { id: 0, pos: { x: 131, y: 209 } },
      { id: 1, pos: { x: 229, y: 391 } },
      { id: 2, pos: { x: 409, y: 209 } },
      { id: 3, pos: { x: 589, y: 431 } },
      { id: 4, pos: { x: 769, y: 229 } },
      { id: 5, pos: { x: 949, y: 451 } },
      { id: 6, pos: { x: 229, y: 295 } },
      { id: 7, pos: { x: 409, y: 305 } },
      { id: 8, pos: { x: 589, y: 335 } },
      { id: 9, pos: { x: 769, y: 325 } },
      { id: 10, pos: { x: 949, y: 355 } },
      { id: 11, pos: { x: 131, y: 305 } },
      { id: 12, pos: { x: 229, y: 199 } },
      { id: 13, pos: { x: 409, y: 401 } },
      { id: 14, pos: { x: 589, y: 239 } },
      { id: 15, pos: { x: 769, y: 421 } },
      { id: 16, pos: { x: 949, y: 259 } },
      { id: 17, pos: { x: 131, y: 401 } },
    ],
    startCash: 1500, // starting budget
    startLives: 5,
    coinReward: 30, // persistent coins for winning Chapter 2 · Episode I
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'frost', count: 3, interval: 1.6, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 0 },
          { enemyType: 'fry', count: 6, interval: 0.5, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 0 },
          { enemyType: 'grunt', count: 5, interval: 1.0, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.6, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.6, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 5, interval: 1.3, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 3 },
          { enemyType: 'dino', count: 4, interval: 0.7, startDelay: 8 },
        ],
      },
    ],
  },
  {
    id: 7,
    chapter: 2,
    episode: 'II',
    name: 'Snowy Caverns',
    subtitle: 'Cavern Mouth',
    background: 'bg-cavern',
    path: [
      { x: 0, y: 520 },
      { x: 200, y: 520 },
      { x: 200, y: 200 },
      { x: 400, y: 200 },
      { x: 400, y: 520 },
      { x: 600, y: 520 },
      { x: 600, y: 200 },
      { x: 800, y: 200 },
      { x: 800, y: 520 },
      { x: 1000, y: 520 },
      { x: 1000, y: 240 },
      { x: 1040, y: 240 },
    ],
    // Inside-corner plots (see Level 1 note): three per serpentine pocket, a
    // column along the cavern's left wall, and a row above the top runs.
    plots: [
      { id: 0, pos: { x: 300, y: 270 } },
      { id: 1, pos: { x: 300, y: 370 } },
      { id: 2, pos: { x: 300, y: 470 } },
      { id: 3, pos: { x: 500, y: 270 } },
      { id: 4, pos: { x: 500, y: 370 } },
      { id: 5, pos: { x: 500, y: 470 } },
      { id: 6, pos: { x: 700, y: 270 } },
      { id: 7, pos: { x: 700, y: 370 } },
      { id: 8, pos: { x: 700, y: 470 } },
      { id: 9, pos: { x: 900, y: 270 } },
      { id: 10, pos: { x: 900, y: 370 } },
      { id: 11, pos: { x: 900, y: 470 } },
      { id: 12, pos: { x: 100, y: 150 } },
      { id: 13, pos: { x: 100, y: 270 } },
      { id: 14, pos: { x: 100, y: 370 } },
      { id: 15, pos: { x: 100, y: 470 } },
      { id: 16, pos: { x: 300, y: 120 } },
      { id: 17, pos: { x: 500, y: 120 } },
      { id: 18, pos: { x: 700, y: 120 } },
      { id: 19, pos: { x: 900, y: 120 } },
    ],
    startCash: 1500, // starting budget
    startLives: 5,
    coinReward: 35, // persistent coins for winning Chapter 2 · Episode II
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'icedino', count: 4, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 0 },
          { enemyType: 'fry', count: 6, interval: 0.5, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 0 },
          { enemyType: 'frost', count: 3, interval: 1.5, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.6, startDelay: 0 },
          { enemyType: 'icedino', count: 5, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
          { enemyType: 'icedino', count: 4, interval: 0.8, startDelay: 7 },
        ],
      },
      {
        groups: [
          { enemyType: 'icedino', count: 8, interval: 0.7, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 3 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 8 },
        ],
      },
    ],
  },
  {
    id: 8,
    chapter: 2,
    episode: 'III',
    name: 'The Icy Igloos',
    subtitle: 'Igloo Village',
    background: 'bg-igloo',
    path: [
      { x: 0, y: 360 },
      { x: 160, y: 360 },
      { x: 160, y: 120 },
      { x: 480, y: 120 },
      { x: 480, y: 400 },
      { x: 320, y: 400 },
      { x: 320, y: 620 },
      { x: 700, y: 620 },
      { x: 700, y: 200 },
      { x: 880, y: 200 },
      { x: 880, y: 500 },
      { x: 1040, y: 500 },
    ],
    // Village-lane pockets (see Level 1 note on inside-corner plots): a north
    // courtyard, the west lane, a centre courtyard, the east pocket, and the
    // far-east ridge by the exit.
    plots: [
      { id: 0, pos: { x: 250, y: 190 } },
      { id: 1, pos: { x: 350, y: 190 } },
      { id: 2, pos: { x: 250, y: 290 } },
      { id: 3, pos: { x: 350, y: 290 } },
      { id: 4, pos: { x: 80, y: 190 } },
      { id: 5, pos: { x: 80, y: 290 } },
      { id: 6, pos: { x: 80, y: 455 } },
      { id: 7, pos: { x: 80, y: 560 } },
      { id: 8, pos: { x: 200, y: 450 } },
      { id: 9, pos: { x: 200, y: 560 } },
      { id: 10, pos: { x: 410, y: 465 } },
      { id: 11, pos: { x: 410, y: 565 } },
      { id: 12, pos: { x: 530, y: 465 } },
      { id: 13, pos: { x: 530, y: 565 } },
      { id: 14, pos: { x: 630, y: 515 } },
      { id: 15, pos: { x: 790, y: 265 } },
      { id: 16, pos: { x: 790, y: 365 } },
      { id: 17, pos: { x: 790, y: 465 } },
      { id: 18, pos: { x: 790, y: 565 } },
      { id: 19, pos: { x: 960, y: 270 } },
      { id: 20, pos: { x: 960, y: 370 } },
      { id: 21, pos: { x: 1005, y: 140 } },
    ],
    startCash: 1500, // starting budget
    startLives: 5,
    coinReward: 40, // persistent coins for winning Chapter 2 · Episode III
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'icedino', count: 5, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 4, interval: 1.5, startDelay: 0 },
          { enemyType: 'icedino', count: 5, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.6, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'icedino', count: 7, interval: 0.8, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 5, interval: 1.4, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 3 },
          { enemyType: 'grunt', count: 5, interval: 1.0, startDelay: 8 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 6, interval: 1.3, startDelay: 0 },
          { enemyType: 'icedino', count: 8, interval: 0.7, startDelay: 3 },
          { enemyType: 'grunt', count: 8, interval: 0.9, startDelay: 8 },
        ],
      },
    ],
  },
  {
    id: 9,
    chapter: 2,
    episode: 'IV',
    name: 'Shady Business',
    subtitle: 'Gemstone Grotto',
    background: 'bg-gemcave',
    // Mineshaft serpentine: in along the top-left gallery, down the west
    // shaft, up the centre shaft, down the east shaft, out the lower right.
    path: [
      { x: 0, y: 120 },
      { x: 240, y: 120 },
      { x: 240, y: 560 },
      { x: 520, y: 560 },
      { x: 520, y: 180 },
      { x: 800, y: 180 },
      { x: 800, y: 560 },
      { x: 1040, y: 560 },
    ],
    // Cave pockets: a single west-wall anchor plus the inner column (the old
    // twin columns crowded the left side — three far-left plots removed), the
    // first pocket, the grand gallery (centre-east), a roof ledge over the top
    // tunnel, and the east chamber.
    plots: [
      { id: 0, pos: { x: 80, y: 210 } },
      { id: 1, pos: { x: 180, y: 260 } },
      { id: 2, pos: { x: 180, y: 360 } },
      { id: 3, pos: { x: 180, y: 460 } },
      { id: 4, pos: { x: 180, y: 560 } },
      { id: 5, pos: { x: 320, y: 230 } },
      { id: 6, pos: { x: 320, y: 330 } },
      { id: 7, pos: { x: 420, y: 280 } },
      { id: 8, pos: { x: 420, y: 380 } },
      { id: 9, pos: { x: 610, y: 270 } },
      { id: 10, pos: { x: 610, y: 370 } },
      { id: 11, pos: { x: 610, y: 470 } },
      { id: 12, pos: { x: 710, y: 320 } },
      { id: 13, pos: { x: 710, y: 420 } },
      { id: 14, pos: { x: 710, y: 520 } },
      { id: 15, pos: { x: 620, y: 110 } },
      { id: 16, pos: { x: 730, y: 110 } },
      { id: 17, pos: { x: 880, y: 250 } },
      { id: 18, pos: { x: 880, y: 350 } },
      { id: 19, pos: { x: 970, y: 300 } },
      { id: 20, pos: { x: 970, y: 400 } },
    ],
    startCash: 1500, // starting budget
    startLives: 5,
    coinReward: 45, // persistent coins for winning Chapter 2 · Episode IV
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'icedino', count: 5, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        // The Dripstone Driller debuts — one slow gem-headed wall.
        groups: [
          { enemyType: 'driller', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'grunt', count: 5, interval: 0.9, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 4, interval: 1.5, startDelay: 0 },
          { enemyType: 'icedino', count: 5, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'icedino', count: 7, interval: 0.8, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.6, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 3 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 8 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.6, startDelay: 0 },
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        // Finale: a drill team bores through with frost and icedino escorts.
        groups: [
          { enemyType: 'driller', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'frost', count: 5, interval: 1.3, startDelay: 3 },
          { enemyType: 'icedino', count: 8, interval: 0.7, startDelay: 8 },
        ],
      },
    ],
  },
  {
    id: 10,
    chapter: 2,
    episode: 'V',
    name: 'The Null Nugget',
    subtitle: 'Lantern Hollow',
    background: 'bg-nullcave',
    // Three lightless shafts strung between lanterns: in along the upper
    // gallery, down the west shaft, up the centre, down again, out lower right.
    path: [
      { x: 0, y: 200 },
      { x: 300, y: 200 },
      { x: 300, y: 520 },
      { x: 620, y: 520 },
      { x: 620, y: 160 },
      { x: 900, y: 160 },
      { x: 900, y: 560 },
      { x: 1040, y: 560 },
    ],
    // Lantern-lit pockets: a shelf above the entry gallery, the west wall
    // columns, the centre hollow, a ridge over the top tunnel, the grand east
    // chamber, and the far-east ledge by the exit.
    plots: [
      { id: 0, pos: { x: 80, y: 120 } },
      { id: 1, pos: { x: 180, y: 120 } },
      { id: 2, pos: { x: 340, y: 120 } },
      { id: 3, pos: { x: 80, y: 290 } },
      { id: 4, pos: { x: 180, y: 290 } },
      { id: 5, pos: { x: 80, y: 390 } },
      { id: 6, pos: { x: 180, y: 390 } },
      { id: 7, pos: { x: 80, y: 490 } },
      { id: 8, pos: { x: 180, y: 490 } },
      { id: 9, pos: { x: 400, y: 270 } },
      { id: 10, pos: { x: 500, y: 270 } },
      { id: 11, pos: { x: 400, y: 370 } },
      { id: 12, pos: { x: 500, y: 370 } },
      { id: 13, pos: { x: 700, y: 90 } },
      { id: 14, pos: { x: 800, y: 90 } },
      { id: 15, pos: { x: 700, y: 250 } },
      { id: 16, pos: { x: 800, y: 250 } },
      { id: 17, pos: { x: 700, y: 350 } },
      { id: 18, pos: { x: 800, y: 350 } },
      { id: 19, pos: { x: 700, y: 450 } },
      { id: 20, pos: { x: 800, y: 450 } },
      { id: 21, pos: { x: 990, y: 240 } },
      { id: 22, pos: { x: 990, y: 340 } },
      { id: 23, pos: { x: 990, y: 440 } },
    ],
    startCash: 2200, // boss-episode war chest — pairs with the 8000 HP Null Nugget
    startLives: 5,
    coinReward: 60, // persistent coins for winning the Chapter 2 finale
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'icedino', count: 5, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'grunt', count: 5, interval: 0.9, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 4, interval: 1.5, startDelay: 0 },
          { enemyType: 'icedino', count: 5, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 2, interval: 1.6, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'icedino', count: 7, interval: 0.8, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.6, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 3 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 8 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'icedino', count: 8, interval: 0.7, startDelay: 4 },
          { enemyType: 'fry', count: 6, interval: 0.4, startDelay: 9 },
        ],
      },
      // The Null Nugget emerges from the dark, alone. It must not reach the
      // lanterns' end — a leak is instant defeat.
      { groups: [{ enemyType: 'nullnugget', count: 1, interval: 1, startDelay: 0 }] },
    ],
  },
  {
    id: 11,
    chapter: 2,
    episode: 'VI',
    name: 'The Mineshafts',
    subtitle: 'Crystal Seam Railway',
    background: 'bg-mineshaft',
    // The one railway level: sleepers + rails instead of the dirt trail.
    trackStyle: 'railway',
    // Four-shaft mining serpentine: in along the lower-left gallery, up the
    // first shaft, down, up again, out along the mid-right drift.
    path: [
      { x: 0, y: 480 },
      { x: 200, y: 480 },
      { x: 200, y: 140 },
      { x: 440, y: 140 },
      { x: 440, y: 480 },
      { x: 680, y: 480 },
      { x: 680, y: 140 },
      { x: 920, y: 140 },
      { x: 920, y: 420 },
      { x: 1040, y: 420 },
    ],
    // Mine pockets: a roof shelf over the top drifts, three timber-braced
    // columns between the shafts, the west wall, a floor row under the lower
    // drifts, and the east chamber by the exit.
    plots: [
      { id: 0, pos: { x: 80, y: 90 } },
      { id: 1, pos: { x: 320, y: 90 } },
      { id: 2, pos: { x: 560, y: 90 } },
      { id: 3, pos: { x: 800, y: 90 } },
      { id: 4, pos: { x: 80, y: 210 } },
      { id: 5, pos: { x: 80, y: 310 } },
      { id: 6, pos: { x: 80, y: 410 } },
      { id: 7, pos: { x: 80, y: 560 } },
      { id: 8, pos: { x: 320, y: 210 } },
      { id: 9, pos: { x: 320, y: 310 } },
      { id: 10, pos: { x: 320, y: 410 } },
      { id: 11, pos: { x: 320, y: 560 } },
      { id: 12, pos: { x: 560, y: 210 } },
      { id: 13, pos: { x: 560, y: 310 } },
      { id: 14, pos: { x: 560, y: 410 } },
      { id: 15, pos: { x: 560, y: 560 } },
      { id: 16, pos: { x: 800, y: 210 } },
      { id: 17, pos: { x: 800, y: 310 } },
      { id: 18, pos: { x: 800, y: 410 } },
      { id: 19, pos: { x: 800, y: 560 } },
      { id: 20, pos: { x: 1000, y: 180 } },
      { id: 21, pos: { x: 1000, y: 300 } },
      { id: 22, pos: { x: 1000, y: 530 } },
    ],
    startCash: 1500, // starting budget
    startLives: 5,
    coinReward: 45, // persistent coins for winning Chapter 2 · Episode VI
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'icedino', count: 5, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        // The Ultra Nugget debuts — one crystal wall with a grunt escort.
        groups: [
          { enemyType: 'ultranugget', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'grunt', count: 5, interval: 0.9, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 4, interval: 1.5, startDelay: 0 },
          { enemyType: 'icedino', count: 5, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'ultranugget', count: 2, interval: 2.0, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'ultranugget', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 3 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 8 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'icedino', count: 7, interval: 0.8, startDelay: 4 },
        ],
      },
      {
        // Finale: a crystal convoy rolls down the rails.
        groups: [
          { enemyType: 'ultranugget', count: 4, interval: 2.0, startDelay: 0 },
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 4 },
          { enemyType: 'icedino', count: 8, interval: 0.7, startDelay: 9 },
        ],
      },
    ],
  },
  {
    id: 12,
    chapter: 2,
    episode: 'VII',
    name: 'The Cobweb Infestation',
    subtitle: 'Webbed Depths',
    background: 'bg-cobweb',
    // Four deep floor-to-ceiling shafts strung with webs — tight 200px
    // spacing so the webbed pockets sit close over the trail; the longest
    // track in the campaign. In along the top-left, out the mid-right.
    path: [
      { x: 0, y: 120 },
      { x: 220, y: 120 },
      { x: 220, y: 600 },
      { x: 420, y: 600 },
      { x: 420, y: 120 },
      { x: 620, y: 120 },
      { x: 620, y: 600 },
      { x: 820, y: 600 },
      { x: 820, y: 300 },
      { x: 1040, y: 300 },
    ],
    // Webbed pockets, listed spread-first (like the inside-corner convention
    // elsewhere): the mid heights of the three inner columns fan out across
    // the whole map before the east chamber, west wall and floor row fill in.
    plots: [
      { id: 0, pos: { x: 320, y: 300 } },
      { id: 1, pos: { x: 520, y: 300 } },
      { id: 2, pos: { x: 720, y: 300 } },
      { id: 3, pos: { x: 320, y: 400 } },
      { id: 4, pos: { x: 520, y: 400 } },
      { id: 5, pos: { x: 720, y: 400 } },
      { id: 6, pos: { x: 320, y: 200 } },
      { id: 7, pos: { x: 520, y: 200 } },
      { id: 8, pos: { x: 720, y: 200 } },
      { id: 9, pos: { x: 320, y: 500 } },
      { id: 10, pos: { x: 520, y: 500 } },
      { id: 11, pos: { x: 720, y: 500 } },
      { id: 12, pos: { x: 920, y: 420 } },
      { id: 13, pos: { x: 920, y: 540 } },
      { id: 14, pos: { x: 920, y: 180 } },
      { id: 15, pos: { x: 1020, y: 180 } },
      { id: 16, pos: { x: 1020, y: 420 } },
      { id: 17, pos: { x: 80, y: 200 } },
      { id: 18, pos: { x: 80, y: 350 } },
      { id: 19, pos: { x: 80, y: 500 } },
      { id: 20, pos: { x: 320, y: 660 } },
      { id: 21, pos: { x: 520, y: 660 } },
      { id: 22, pos: { x: 720, y: 660 } },
    ],
    startCash: 1500, // starting budget
    startLives: 5,
    coinReward: 50, // persistent coins for winning Chapter 2 · Episode VII
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'icedino', count: 4, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        // The first Nugget Jockey skitters out of the webs — this is their level.
        groups: [
          { enemyType: 'jockey', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'grunt', count: 5, interval: 0.9, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'jockey', count: 2, interval: 2.4, startDelay: 0 },
          { enemyType: 'icedino', count: 5, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'jockey', count: 3, interval: 2.0, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 5 },
        ],
      },
      {
        // A crystal wall wanders in from the mineshafts next door.
        groups: [
          { enemyType: 'ultranugget', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'jockey', count: 3, interval: 2.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'jockey', count: 4, interval: 1.8, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'ultranugget', count: 2, interval: 2.0, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 4 },
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 9 },
        ],
      },
      {
        // Finale: the full infestation pours out of the webs.
        groups: [
          { enemyType: 'jockey', count: 8, interval: 1.4, startDelay: 0 },
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 5 },
          { enemyType: 'fry', count: 6, interval: 0.4, startDelay: 11 },
        ],
      },
    ],
  },
  {
    id: 13,
    chapter: 2,
    episode: 'VIII',
    name: 'Amethyst Core',
    subtitle: 'The Geode Heart',
    background: 'bg-amethyst',
    // Piles of amethyst crystals along the track and ringing every plot (the
    // geode heart of the mountain) — see GameScene's crystal decor pass.
    decor: 'crystals',
    // Three deep crystal shafts through the core: in along the mid-west
    // gallery, up to the north drift, down the great south shaft, up again,
    // and out the lower-east vein.
    path: [
      { x: 0, y: 320 },
      { x: 160, y: 320 },
      { x: 160, y: 120 },
      { x: 400, y: 120 },
      { x: 400, y: 560 },
      { x: 640, y: 560 },
      { x: 640, y: 160 },
      { x: 880, y: 160 },
      { x: 880, y: 520 },
      { x: 1040, y: 520 },
    ],
    // Geode pockets: the west wall by the entry, three crystal-ringed columns
    // between the shafts, a roof ledge over the centre drift, a floor row
    // under the south run, and the east chamber by the exit.
    plots: [
      { id: 0, pos: { x: 280, y: 210 } },
      { id: 1, pos: { x: 280, y: 310 } },
      { id: 2, pos: { x: 280, y: 410 } },
      { id: 3, pos: { x: 280, y: 510 } },
      { id: 4, pos: { x: 80, y: 120 } },
      { id: 5, pos: { x: 80, y: 220 } },
      { id: 6, pos: { x: 80, y: 420 } },
      { id: 7, pos: { x: 80, y: 520 } },
      { id: 8, pos: { x: 520, y: 90 } },
      { id: 9, pos: { x: 520, y: 210 } },
      { id: 10, pos: { x: 520, y: 310 } },
      { id: 11, pos: { x: 520, y: 410 } },
      { id: 12, pos: { x: 760, y: 220 } },
      { id: 13, pos: { x: 760, y: 320 } },
      { id: 14, pos: { x: 760, y: 420 } },
      { id: 15, pos: { x: 760, y: 520 } },
      { id: 16, pos: { x: 960, y: 240 } },
      { id: 17, pos: { x: 960, y: 340 } },
      { id: 18, pos: { x: 960, y: 440 } },
      { id: 19, pos: { x: 1000, y: 120 } },
      { id: 20, pos: { x: 280, y: 610 } },
      { id: 21, pos: { x: 460, y: 640 } },
      { id: 22, pos: { x: 560, y: 640 } },
    ],
    startCash: 1500, // starting budget
    startLives: 5,
    coinReward: 55, // persistent coins for winning Chapter 2 · Episode VIII
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'icedino', count: 5, interval: 0.9, startDelay: 0 },
          { enemyType: 'grunt', count: 4, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'fry', count: 8, interval: 0.5, startDelay: 0 },
          { enemyType: 'dino', count: 5, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        // The Crystal Watcher debuts — one hovering hex-crystal with an escort.
        groups: [
          { enemyType: 'watcher', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'grunt', count: 5, interval: 0.9, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'icedino', count: 5, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'watcher', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'ultranugget', count: 1, interval: 1.0, startDelay: 2 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'jockey', count: 3, interval: 2.0, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'watcher', count: 2, interval: 6.0, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'hotdog', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'icedino', count: 7, interval: 0.8, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'ultranugget', count: 2, interval: 2.0, startDelay: 0 },
          { enemyType: 'jockey', count: 3, interval: 1.8, startDelay: 4 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 9 },
        ],
      },
      {
        // Finale: the geode wakes — a watcher trio drifts out with a convoy.
        groups: [
          { enemyType: 'watcher', count: 3, interval: 6.0, startDelay: 0 },
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 5 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 10 },
        ],
      },
    ],
  },
  {
    id: 14,
    chapter: 2,
    episode: 'IX',
    name: "The Enemy's Fortress",
    subtitle: "The King's Gate",
    background: 'bg-crystalwarren',
    // A cave of orange crystal veins and huge moss-cloaked boulders. A castle
    // door crowns the top of the cavern — the entrance to King Nugget-tron's
    // hall (Episode X). Four crystal galleries switch back through the warren.
    path: [
      { x: 0, y: 300 },
      { x: 180, y: 300 },
      { x: 180, y: 120 },
      { x: 430, y: 120 },
      { x: 430, y: 540 },
      { x: 680, y: 540 },
      { x: 680, y: 140 },
      { x: 930, y: 140 },
      { x: 930, y: 480 },
      { x: 1040, y: 480 },
    ],
    plots: [
      { id: 0, pos: { x: 52, y: 358 } },
      { id: 1, pos: { x: 156, y: 358 } },
      { id: 2, pos: { x: 238, y: 222 } },
      { id: 3, pos: { x: 362, y: 178 } },
      { id: 4, pos: { x: 372, y: 302 } },
      { id: 5, pos: { x: 372, y: 406 } },
      { id: 6, pos: { x: 372, y: 510 } },
      { id: 7, pos: { x: 482, y: 482 } },
      { id: 8, pos: { x: 586, y: 482 } },
      { id: 9, pos: { x: 664, y: 598 } },
      { id: 10, pos: { x: 738, y: 462 } },
      { id: 11, pos: { x: 738, y: 358 } },
      { id: 12, pos: { x: 738, y: 254 } },
      { id: 13, pos: { x: 836, y: 198 } },
      { id: 14, pos: { x: 988, y: 192 } },
      { id: 15, pos: { x: 988, y: 296 } },
      { id: 16, pos: { x: 988, y: 400 } },
      { id: 17, pos: { x: 872, y: 478 } },
      { id: 18, pos: { x: 1008, y: 538 } },
      { id: 19, pos: { x: 52, y: 242 } },
    ],
    startCash: 1600,
    startLives: 5,
    coinReward: 65, // persistent coins for winning Chapter 2 · Episode IX
    waves: [
      { groups: [{ enemyType: 'grunt', count: 6, interval: 1.1, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 0 },
          { enemyType: 'fry', count: 6, interval: 0.5, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 0 },
          { enemyType: 'grunt', count: 5, interval: 1.0, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 3, interval: 1.6, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'jockey', count: 2, interval: 2.2, startDelay: 0 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'ultranugget', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 2 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 6 },
        ],
      },
      {
        // The Nugget Guard is mustered from the castle gate — its first sortie.
        groups: [
          { enemyType: 'nuggetguard', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'nuggetguard', count: 2, interval: 3.0, startDelay: 0 },
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'watcher', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'nuggetguard', count: 2, interval: 3.0, startDelay: 2 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 5 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'nuggetguard', count: 3, interval: 2.6, startDelay: 3 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 6 },
        ],
      },
      {
        groups: [
          { enemyType: 'jockey', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'nuggetguard', count: 3, interval: 2.6, startDelay: 3 },
          { enemyType: 'frost', count: 3, interval: 1.4, startDelay: 7 },
        ],
      },
      {
        // Finale: the full castle guard turns out with a crystal convoy.
        groups: [
          { enemyType: 'nuggetguard', count: 4, interval: 2.2, startDelay: 0 },
          { enemyType: 'ultranugget', count: 2, interval: 2.0, startDelay: 4 },
          { enemyType: 'icedino', count: 8, interval: 0.6, startDelay: 9 },
        ],
      },
    ],
  },
  {
    id: 15,
    chapter: 2,
    episode: 'X',
    name: 'Throne of Nugget-tron',
    subtitle: 'The King Falls',
    background: 'bg-throne',
    // Inside the castle throne room: a long winding processional ringed by
    // gemstones of every colour. The final level of Chapter 2 — five galleries
    // switch back and forth before the exit, with plots crammed into every
    // alcove for the fight of the campaign.
    path: [
      { x: 0, y: 340 },
      { x: 140, y: 340 },
      { x: 140, y: 140 },
      { x: 360, y: 140 },
      { x: 360, y: 560 },
      { x: 560, y: 560 },
      { x: 560, y: 140 },
      { x: 760, y: 140 },
      { x: 760, y: 560 },
      { x: 960, y: 560 },
      { x: 960, y: 220 },
      { x: 1040, y: 220 },
    ],
    plots: [
      { id: 0, pos: { x: 52, y: 398 } },
      { id: 1, pos: { x: 78, y: 282 } },
      { id: 2, pos: { x: 198, y: 314 } },
      { id: 3, pos: { x: 218, y: 198 } },
      { id: 4, pos: { x: 418, y: 192 } },
      { id: 5, pos: { x: 302, y: 322 } },
      { id: 6, pos: { x: 418, y: 348 } },
      { id: 7, pos: { x: 302, y: 478 } },
      { id: 8, pos: { x: 418, y: 504 } },
      { id: 9, pos: { x: 386, y: 618 } },
      { id: 10, pos: { x: 516, y: 618 } },
      { id: 11, pos: { x: 618, y: 508 } },
      { id: 12, pos: { x: 502, y: 404 } },
      { id: 13, pos: { x: 618, y: 352 } },
      { id: 14, pos: { x: 502, y: 248 } },
      { id: 15, pos: { x: 618, y: 196 } },
      { id: 16, pos: { x: 818, y: 218 } },
      { id: 17, pos: { x: 702, y: 270 } },
      { id: 18, pos: { x: 818, y: 374 } },
      { id: 19, pos: { x: 702, y: 426 } },
      { id: 20, pos: { x: 812, y: 502 } },
      { id: 21, pos: { x: 864, y: 618 } },
      { id: 22, pos: { x: 1018, y: 534 } },
      { id: 23, pos: { x: 902, y: 456 } },
      { id: 24, pos: { x: 1018, y: 404 } },
      { id: 25, pos: { x: 902, y: 300 } },
      { id: 26, pos: { x: 1012, y: 278 } },
      { id: 27, pos: { x: 82, y: 158 } },
    ],
    startCash: 2200,
    startLives: 5,
    coinReward: 90, // persistent coins for winning Chapter 2 · Episode X (finale)
    waves: [
      { groups: [{ enemyType: 'grunt', count: 8, interval: 1.0, startDelay: 0 }] },
      {
        groups: [
          { enemyType: 'dino', count: 8, interval: 0.6, startDelay: 0 },
          { enemyType: 'fry', count: 6, interval: 0.5, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'icedino', count: 6, interval: 0.8, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'frost', count: 3, interval: 1.6, startDelay: 0 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 2 },
        ],
      },
      {
        groups: [
          { enemyType: 'jockey', count: 2, interval: 2.2, startDelay: 0 },
          { enemyType: 'dino', count: 8, interval: 0.6, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'nuggetguard', count: 2, interval: 3.0, startDelay: 0 },
          { enemyType: 'grunt', count: 6, interval: 0.9, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 2, interval: 1.8, startDelay: 0 },
          { enemyType: 'icedino', count: 8, interval: 0.7, startDelay: 3 },
        ],
      },
      {
        groups: [
          { enemyType: 'watcher', count: 1, interval: 1.0, startDelay: 0 },
          { enemyType: 'nuggetguard', count: 2, interval: 3.0, startDelay: 2 },
          { enemyType: 'fry', count: 8, interval: 0.4, startDelay: 6 },
        ],
      },
      {
        groups: [
          { enemyType: 'ultranugget', count: 2, interval: 2.0, startDelay: 0 },
          { enemyType: 'jockey', count: 3, interval: 1.8, startDelay: 4 },
        ],
      },
      {
        groups: [
          { enemyType: 'nuggetguard', count: 3, interval: 2.6, startDelay: 0 },
          { enemyType: 'frost', count: 4, interval: 1.4, startDelay: 4 },
          { enemyType: 'dino', count: 6, interval: 0.7, startDelay: 8 },
        ],
      },
      {
        groups: [
          { enemyType: 'driller', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'nuggetguard', count: 3, interval: 2.6, startDelay: 4 },
          { enemyType: 'icedino', count: 8, interval: 0.7, startDelay: 8 },
        ],
      },
      {
        groups: [
          { enemyType: 'watcher', count: 2, interval: 6.0, startDelay: 0 },
          { enemyType: 'ultranugget', count: 2, interval: 2.0, startDelay: 4 },
          { enemyType: 'grunt', count: 8, interval: 0.8, startDelay: 8 },
        ],
      },
      {
        groups: [
          { enemyType: 'jockey', count: 4, interval: 1.6, startDelay: 0 },
          { enemyType: 'nuggetguard', count: 4, interval: 2.2, startDelay: 4 },
          { enemyType: 'fry', count: 10, interval: 0.35, startDelay: 9 },
        ],
      },
      {
        // The last of the royal host — everything the castle has left.
        groups: [
          { enemyType: 'driller', count: 3, interval: 1.8, startDelay: 0 },
          { enemyType: 'ultranugget', count: 3, interval: 2.0, startDelay: 4 },
          { enemyType: 'nuggetguard', count: 4, interval: 2.2, startDelay: 9 },
        ],
      },
      {
        // The King himself: King Nugget-tron marches alone. He cannot be slowed
        // or frozen, and his decree stuns a tower every few seconds — out-damage
        // him before he reaches the throne-room exit or the campaign is lost.
        groups: [{ enemyType: 'kingnuggetron', count: 1, interval: 1.0, startDelay: 0 }],
      },
    ],
  },
  {
    // Chapter 3 · Episode I — Hambi Castle. The nuggets are defeated; a new
    // vegetable army musters at the castle gates. A castle-themed serpentine
    // with no boss (this isn't the chapter finale) — the tanky Potato Slicer
    // headlines the later waves. No music yet (added later).
    id: 16,
    chapter: 3,
    episode: 'I',
    name: 'Hambi Castle',
    subtitle: 'The Castle Gates',
    background: 'bg-hambicastle',
    path: [
      { x: 0, y: 200 },
      { x: 220, y: 200 },
      { x: 220, y: 500 },
      { x: 440, y: 500 },
      { x: 440, y: 180 },
      { x: 660, y: 180 },
      { x: 660, y: 520 },
      { x: 880, y: 520 },
      { x: 880, y: 220 },
      { x: 1035, y: 220 },
    ],
    plots: [
      { id: 0, pos: { x: 330, y: 260 } },
      { id: 1, pos: { x: 330, y: 440 } },
      { id: 2, pos: { x: 550, y: 300 } },
      { id: 3, pos: { x: 550, y: 460 } },
      { id: 4, pos: { x: 770, y: 260 } },
      { id: 5, pos: { x: 770, y: 440 } },
      { id: 6, pos: { x: 110, y: 320 } },
      { id: 7, pos: { x: 110, y: 470 } },
      { id: 8, pos: { x: 550, y: 120 } },
      { id: 9, pos: { x: 960, y: 360 } },
      { id: 10, pos: { x: 960, y: 130 } },
    ],
    // 2600 (not 2000): the vegetable roster is far tankier than Chapter 2, and
    // the balance-sanity bot only clears this on 2600+ — landing it in the
    // "beatable but hard" bracket (a naive full-roster bot barely wins, ~2 of 5
    // lives; 2800 lets it cruise). See balance-bracket.test.ts.
    startCash: 2600,
    startLives: 5,
    coinReward: 65,
    waves: [
      // 1 — light intro: the Carrot Crushers rush the gate.
      { groups: [{ enemyType: 'carrotcrusher', count: 5, interval: 1.2, startDelay: 0 }] },
      // 2 — more carrots.
      { groups: [{ enemyType: 'carrotcrusher', count: 7, interval: 1.0, startDelay: 0 }] },
      // 3 — the first Brocco arrives; the Veggie Hex debuts.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 0 },
          { enemyType: 'brocco', count: 1, interval: 1, startDelay: 3 },
        ],
      },
      // 4 — Broccos escort a carrot pack.
      {
        groups: [
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 1.0, startDelay: 2 },
        ],
      },
      // 5 — a carrot swarm.
      { groups: [{ enemyType: 'carrotcrusher', count: 9, interval: 0.8, startDelay: 0 }] },
      // 6 — heavier hex pressure.
      {
        groups: [
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 1.0, startDelay: 3 },
        ],
      },
      // 7 — the first Potato Slicers lumber in.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 2 },
        ],
      },
      // 8 — Potato Slicers under Brocco cover.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 4 },
        ],
      },
      // 9 — all three, mixed.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.7, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 4, startDelay: 2 },
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 6 },
        ],
      },
      // 10 — heavier mixed push.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
        ],
      },
      // 11 — finale surge: the biggest mixed wave of all three.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 10, interval: 0.6, startDelay: 0 },
          { enemyType: 'brocco', count: 4, interval: 2.5, startDelay: 3 },
          { enemyType: 'potatoslicer', count: 5, interval: 2, startDelay: 5 },
        ],
      },
    ],
  },
  {
    // Chapter 3 · Episode II — The Hambi Village. The veggie army pushes past
    // the castle into the farmland Hambis call home: houses and their Hambi
    // residents ring an open field. No new enemies — the castle roster
    // (carrot / brocco / potato) returns across ten waves, no boss.
    id: 17,
    chapter: 3,
    episode: 'II',
    name: 'The Hambi Village',
    subtitle: 'Fields and Homesteads',
    background: 'bg-hambivillage',
    // The old route ran along the top edge (y=120, x=0→300) and hugged the left
    // edge (x=120, y=340→600) — straight through the two bands the village art
    // fills with huts, so the trail was drawn over the houses. This route keeps
    // the whole top strip (y < 165) and the outer left/right margins clear of
    // everything but the entry/exit road, leaving the art a frame to live in;
    // the extra switchbacks hold the length at 2575px (was 2455), so enemies
    // still get the same long walk despite the smaller footprint.
    path: [
      { x: 0, y: 240 },
      { x: 200, y: 240 },
      { x: 200, y: 470 },
      { x: 380, y: 470 },
      { x: 380, y: 210 },
      { x: 560, y: 210 },
      { x: 560, y: 600 },
      { x: 760, y: 600 },
      { x: 760, y: 210 },
      { x: 940, y: 210 },
      { x: 940, y: 480 },
      { x: 1035, y: 480 },
    ],
    // Every plot sits within ~120px of the trail. The shortest tower ranges are
    // 110–140 (cheese 110, bacon 120, patty/blade/ninja 140), so a plot much
    // further out than this is dead ground — a tower placed there can never
    // reach the path. Hambi Castle keeps every plot ≤110px out; these match.
    // Six columns of three in the pockets the switchbacks carve out; none sits
    // above y=180, so the hut band along the top stays unobstructed.
    plots: [
      { id: 0, pos: { x: 90, y: 180 } },
      { id: 1, pos: { x: 110, y: 350 } },
      { id: 2, pos: { x: 110, y: 540 } },
      { id: 3, pos: { x: 290, y: 180 } },
      { id: 4, pos: { x: 290, y: 350 } },
      { id: 5, pos: { x: 290, y: 570 } },
      { id: 6, pos: { x: 470, y: 350 } },
      { id: 7, pos: { x: 470, y: 480 } },
      { id: 8, pos: { x: 470, y: 620 } },
      { id: 9, pos: { x: 660, y: 250 } },
      { id: 10, pos: { x: 660, y: 400 } },
      { id: 11, pos: { x: 660, y: 540 } },
      { id: 12, pos: { x: 860, y: 350 } },
      { id: 13, pos: { x: 860, y: 480 } },
      { id: 14, pos: { x: 860, y: 620 } },
      { id: 15, pos: { x: 1010, y: 180 } },
      { id: 16, pos: { x: 1010, y: 300 } },
      { id: 17, pos: { x: 990, y: 570 } },
    ],
    // Player-directed: 2500 (was 3100, itself tuned to sit just above the
    // sanity bot's ~3050 cliff on the old 13-plot layout). The five extra plots
    // and the longer 2575px route pay for most of the cut — see the plot note
    // above — so the level opens leaner and is built up out of bounty income
    // rather than front-loaded from the war chest.
    startCash: 2500,
    startLives: 5,
    coinReward: 70,
    waves: [
      // 1 — the carrots reach the village fence.
      { groups: [{ enemyType: 'carrotcrusher', count: 6, interval: 1.1, startDelay: 0 }] },
      // 2 — a bigger carrot push.
      { groups: [{ enemyType: 'carrotcrusher', count: 8, interval: 0.95, startDelay: 0 }] },
      // 3 — Broccos join; the Veggie Hex returns.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 3 },
        ],
      },
      // 4 — the first Potato Slicers lumber between the houses.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 2 },
        ],
      },
      // 5 — a carrot swarm floods the field.
      { groups: [{ enemyType: 'carrotcrusher', count: 10, interval: 0.75, startDelay: 0 }] },
      // 6 — heavier hex pressure over a carrot pack.
      {
        groups: [
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 7, interval: 0.9, startDelay: 3 },
        ],
      },
      // 7 — Potato Slicers under Brocco cover.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 4 },
        ],
      },
      // 8 — all three, mixed.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.7, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 4, startDelay: 2 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 6 },
        ],
      },
      // 9 — a heavy armoured push on the homesteads.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
        ],
      },
      // 10 — finale surge: the whole village roster at once.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 10, interval: 0.6, startDelay: 0 },
          { enemyType: 'brocco', count: 4, interval: 2.5, startDelay: 3 },
          { enemyType: 'potatoslicer', count: 5, interval: 2, startDelay: 5 },
        ],
      },
    ],
  },
  {
    // Chapter 3 · Episode III — The Gloomy Forests. The retreating vegetables
    // regroup in a fog-shrouded wood beside a river. The Carrot Ninja debuts
    // (wave 3): a Carrot Crusher that keeps the rusher's speed but costs more
    // to kill. Twelve waves, the chapter's hardest — but still no boss.
    id: 18,
    chapter: 3,
    episode: 'III',
    name: 'The Gloomy Forests',
    subtitle: 'The Misted River',
    background: 'bg-gloomyforest',
    path: [
      { x: 0, y: 360 },
      { x: 180, y: 360 },
      { x: 180, y: 140 },
      { x: 420, y: 140 },
      { x: 420, y: 560 },
      { x: 660, y: 560 },
      { x: 660, y: 180 },
      { x: 900, y: 180 },
      { x: 900, y: 460 },
      { x: 1035, y: 460 },
    ],
    // As on the village (id 17): every plot hugs the trail within ~125px so the
    // short-range towers can actually reach it. Ids 15–19 fill the mid-corridor
    // pockets the original 15 left empty (player-directed: more plots).
    plots: [
      { id: 0, pos: { x: 80, y: 260 } },
      { id: 1, pos: { x: 80, y: 460 } },
      { id: 2, pos: { x: 300, y: 240 } },
      { id: 3, pos: { x: 320, y: 450 } },
      { id: 4, pos: { x: 420, y: 660 } },
      { id: 5, pos: { x: 540, y: 460 } },
      { id: 6, pos: { x: 540, y: 240 } },
      { id: 7, pos: { x: 540, y: 660 } },
      { id: 8, pos: { x: 780, y: 280 } },
      { id: 9, pos: { x: 780, y: 90 } },
      { id: 10, pos: { x: 760, y: 560 } },
      { id: 11, pos: { x: 1000, y: 280 } },
      { id: 12, pos: { x: 980, y: 100 } },
      { id: 13, pos: { x: 960, y: 570 } },
      { id: 14, pos: { x: 660, y: 660 } },
      { id: 15, pos: { x: 300, y: 350 } },
      { id: 16, pos: { x: 540, y: 350 } },
      { id: 17, pos: { x: 780, y: 460 } },
      { id: 18, pos: { x: 1000, y: 380 } },
      { id: 19, pos: { x: 540, y: 100 } },
    ],
    // Tuned against balance-bracket.test.ts' sanity bot, and well above the
    // village: two extra waves, and the Ninja's 1050 HP at the Crusher's speed
    // is a lot more to chew through. Under the original tower tuning 4200 lost
    // outright and 4400 scraped by on a single life on one seed; 4400 is the
    // 2026-07-16 player-directed cut from 4600 (they asked for 3500, but the
    // bracket proves that unwinnable), leaning on the same-day Ninja/Dark Hambi
    // buffs to keep every seed winnable — the chapter's hardest, as designed.
    startCash: 4400,
    startLives: 5,
    coinReward: 80,
    waves: [
      // 1 — carrots slip out of the mist.
      { groups: [{ enemyType: 'carrotcrusher', count: 6, interval: 1.1, startDelay: 0 }] },
      // 2 — Broccos brood among the trees.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 6, interval: 0.95, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 3 },
        ],
      },
      // 3 — the Carrot Ninja debuts: two of them, flanking a carrot pack.
      {
        groups: [
          { enemyType: 'carrotninja', count: 2, interval: 2.5, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 3 },
        ],
      },
      // 4 — Potato Slicers arrive under Ninja escort.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 3, interval: 2, startDelay: 2 },
        ],
      },
      // 5 — a mixed carrot swarm: Crushers and Ninjas together.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 9, interval: 0.75, startDelay: 0 },
          { enemyType: 'carrotninja', count: 3, interval: 2, startDelay: 4 },
        ],
      },
      // 6 — hex pressure over a Ninja pack.
      {
        groups: [
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 4, interval: 1.8, startDelay: 3 },
        ],
      },
      // 7 — armour and blades: Slicers behind a Ninja screen.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 4, interval: 1.8, startDelay: 2 },
        ],
      },
      // 8 — all four types, mixed.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.7, startDelay: 0 },
          { enemyType: 'carrotninja', count: 3, interval: 2, startDelay: 2 },
          { enemyType: 'brocco', count: 2, interval: 4, startDelay: 4 },
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 6 },
        ],
      },
      // 9 — a Ninja rush under hex cover.
      {
        groups: [
          { enemyType: 'carrotninja', count: 6, interval: 1.4, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
        ],
      },
      // 10 — the heavy column: Slicers and Broccos.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
          { enemyType: 'carrotninja', count: 4, interval: 1.8, startDelay: 5 },
        ],
      },
      // 11 — a blade storm: the biggest Ninja wave.
      {
        groups: [
          { enemyType: 'carrotninja', count: 8, interval: 1.2, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 4 },
        ],
      },
      // 12 — finale surge: everything the forest has left.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 10, interval: 0.6, startDelay: 0 },
          { enemyType: 'carrotninja', count: 6, interval: 1.2, startDelay: 2 },
          { enemyType: 'brocco', count: 4, interval: 2.5, startDelay: 4 },
          { enemyType: 'potatoslicer', count: 5, interval: 2, startDelay: 6 },
        ],
      },
    ],
  },
  {
    // Chapter 3 · Episode IV — The Soy Sauce Desert. The veggie army falls back
    // across a soy-dark desert toward the emperor's temple, cactuses lining the
    // caravan road. The Dry Carrot debuts (wave 3): a dust-caked rotten carrot,
    // faster than the Ninja, whose desert hex disables one random tower for 2s
    // every 10s. Eleven waves.
    id: 19,
    chapter: 3,
    episode: 'IV',
    name: 'The Soy Sauce Desert',
    subtitle: 'The Temple Road',
    background: 'bg-soysaucedesert',
    path: [
      { x: 0, y: 200 },
      { x: 260, y: 200 },
      { x: 260, y: 520 },
      { x: 540, y: 520 },
      { x: 540, y: 160 },
      { x: 820, y: 160 },
      { x: 820, y: 560 },
      { x: 1040, y: 560 },
    ],
    // Player-directed (2026-07-17): more plots, hugging the caravan road tight
    // (~55px off the trail vs the old 70-120px) and packed closer together, so
    // short-range towers actually reach the road. 19 pockets line both shoulders
    // of the serpentine; two sit in the central bay to cover the top sweep.
    plots: [
      { id: 0, pos: { x: 78, y: 146 } }, // above the top sweep, left
      { id: 1, pos: { x: 186, y: 146 } }, // above the top sweep, right
      { id: 2, pos: { x: 204, y: 300 } }, // left of the first descent
      { id: 3, pos: { x: 204, y: 420 } }, // left of the first descent
      { id: 4, pos: { x: 318, y: 268 } }, // right of the first descent
      { id: 5, pos: { x: 318, y: 392 } }, // right of the first descent
      { id: 6, pos: { x: 360, y: 576 } }, // below the low sweep
      { id: 7, pos: { x: 470, y: 576 } }, // below the low sweep
      { id: 8, pos: { x: 484, y: 258 } }, // left of the climb
      { id: 9, pos: { x: 484, y: 410 } }, // left of the climb
      { id: 10, pos: { x: 598, y: 104 } }, // above the high sweep, left
      { id: 11, pos: { x: 706, y: 104 } }, // above the high sweep, right
      { id: 12, pos: { x: 650, y: 300 } }, // central bay
      { id: 13, pos: { x: 650, y: 430 } }, // central bay
      { id: 14, pos: { x: 764, y: 258 } }, // left of the final descent
      { id: 15, pos: { x: 764, y: 470 } }, // left of the final descent
      { id: 16, pos: { x: 878, y: 360 } }, // right of the final descent
      { id: 17, pos: { x: 906, y: 504 } }, // above the exit sweep
      { id: 18, pos: { x: 1004, y: 616 } }, // below the exit sweep
    ],
    // Player-directed: $4600 to open, one notch above the forests' $4400 — the
    // Dry Carrot's 105-speed hex packs punish thin coverage. Tuned against
    // balance-bracket.test.ts' sanity bot like the rest of the chapter.
    startCash: 4600,
    startLives: 5,
    coinReward: 85,
    waves: [
      // 1 — carrots shimmer out of the heat haze.
      { groups: [{ enemyType: 'carrotcrusher', count: 6, interval: 1.0, startDelay: 0 }] },
      // 2 — Broccos trudge the caravan road.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 3 },
        ],
      },
      // 3 — the Dry Carrot debuts: a lone dusty husk leading a carrot pack.
      {
        groups: [
          { enemyType: 'drycarrot', count: 1, interval: 2.5, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 5, interval: 0.9, startDelay: 3 },
        ],
      },
      // 4 — Slicers under a dry escort.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'drycarrot', count: 2, interval: 2, startDelay: 2 },
        ],
      },
      // 5 — a crusher swarm with Ninja flankers.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.8, startDelay: 0 },
          { enemyType: 'carrotninja', count: 3, interval: 2, startDelay: 3 },
        ],
      },
      // 6 — hex pressure: Broccos slow the guns while Dry Carrots jam them.
      {
        groups: [
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'drycarrot', count: 2, interval: 1.8, startDelay: 3 },
        ],
      },
      // 7 — armour and blades: Slicers behind a Ninja screen.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 4, interval: 1.8, startDelay: 2 },
        ],
      },
      // 8 — the dust storm: crushers, husks and hexes together.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.7, startDelay: 0 },
          { enemyType: 'drycarrot', count: 2, interval: 2, startDelay: 2 },
          { enemyType: 'brocco', count: 2, interval: 4, startDelay: 4 },
        ],
      },
      // 9 — a Dry Carrot rush with armoured backup.
      {
        groups: [
          { enemyType: 'drycarrot', count: 3, interval: 1.5, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 3 },
        ],
      },
      // 10 — the heavy column marches on the temple gate.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
          { enemyType: 'carrotninja', count: 3, interval: 1.8, startDelay: 5 },
        ],
      },
      // 11 — finale surge: everything the desert can muster.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.7, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 1.3, startDelay: 2 },
          { enemyType: 'brocco', count: 3, interval: 2.5, startDelay: 4 },
          { enemyType: 'potatoslicer', count: 4, interval: 2, startDelay: 6 },
        ],
      },
    ],
  },
  {
    // Chapter 3 · Episode V — The Emperor's Temple. Inside the sandstone
    // sanctum, carrot guards flanking the great doors, the campaign's final
    // stand ends at Emperor Pepro: a red pepper on a sandy cloud whose
    // Imperial Decree seals tower placement for 10s every 15s and whose court
    // of Mini Bells (up to 3) refreshes every 10s. Fifteen waves.
    id: 20,
    chapter: 3,
    episode: 'V',
    name: "The Emperor's Temple",
    subtitle: 'The Inner Sanctum',
    background: 'bg-emperortemple',
    // A long processional serpentine down the temple nave — four full-width
    // sweeps (~3240px, the campaign's longest) so the fast-floating emperor
    // stays under fire, with plot bands midway between sweeps so every tower
    // covers two lanes of the procession (the arena's banding trick).
    path: [
      { x: 0, y: 140 },
      { x: 980, y: 140 },
      { x: 980, y: 320 },
      { x: 140, y: 320 },
      { x: 140, y: 500 },
      { x: 980, y: 500 },
      { x: 980, y: 660 },
      { x: 1040, y: 660 },
    ],
    plots: [
      { id: 0, pos: { x: 240, y: 230 } },
      { id: 1, pos: { x: 360, y: 230 } },
      { id: 2, pos: { x: 480, y: 230 } },
      { id: 3, pos: { x: 600, y: 230 } },
      { id: 4, pos: { x: 720, y: 230 } },
      { id: 5, pos: { x: 840, y: 230 } },
      { id: 6, pos: { x: 280, y: 410 } },
      { id: 7, pos: { x: 400, y: 410 } },
      { id: 8, pos: { x: 520, y: 410 } },
      { id: 9, pos: { x: 640, y: 410 } },
      { id: 10, pos: { x: 760, y: 410 } },
      { id: 11, pos: { x: 880, y: 410 } },
      { id: 12, pos: { x: 240, y: 580 } },
      { id: 13, pos: { x: 360, y: 580 } },
      { id: 14, pos: { x: 480, y: 580 } },
      { id: 15, pos: { x: 600, y: 580 } },
      { id: 16, pos: { x: 720, y: 580 } },
      { id: 17, pos: { x: 840, y: 580 } },
    ],
    // Player-directed: $5000 to open the boss stand — the decree windows mean
    // whatever isn't built between seals doesn't get built at all.
    startCash: 5000,
    startLives: 5,
    coinReward: 100,
    waves: [
      // 1 — the temple guard probes the intruders.
      { groups: [{ enemyType: 'carrotcrusher', count: 6, interval: 1.0, startDelay: 0 }] },
      // 2 — Ninjas slip between the pillars.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 0 },
          { enemyType: 'carrotninja', count: 2, interval: 2.5, startDelay: 3 },
        ],
      },
      // 3 — Dry Carrots carry the desert's dust inside.
      {
        groups: [
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 5, interval: 0.9, startDelay: 2 },
        ],
      },
      // 4 — hexes echo down the corridors.
      {
        groups: [
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 4, interval: 1.8, startDelay: 2 },
        ],
      },
      // 5 — Slicers under dry escort.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 3 },
        ],
      },
      // 6 — a crusher column charges the nave.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 9, interval: 0.75, startDelay: 0 },
          { enemyType: 'carrotninja', count: 3, interval: 2, startDelay: 4 },
        ],
      },
      // 7 — the double hex: dust and broccoli.
      {
        groups: [
          { enemyType: 'drycarrot', count: 4, interval: 1.8, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
        ],
      },
      // 8 — armour and blades at the sanctum steps.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 4, interval: 1.8, startDelay: 2 },
        ],
      },
      // 9 — the guard commits: crushers, husks and hexes.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.7, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 2 },
          { enemyType: 'brocco', count: 2, interval: 4, startDelay: 5 },
        ],
      },
      // 10 — a Ninja rush under hex cover.
      {
        groups: [
          { enemyType: 'carrotninja', count: 6, interval: 1.4, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
        ],
      },
      // 11 — Dry Carrots sprint the cloister.
      {
        groups: [
          { enemyType: 'drycarrot', count: 5, interval: 1.5, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 3 },
        ],
      },
      // 12 — the heavy column: the temple's last armour.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
          { enemyType: 'carrotninja', count: 4, interval: 1.8, startDelay: 5 },
        ],
      },
      // 13 — the dust storm breaks against the throne room doors.
      {
        groups: [
          { enemyType: 'drycarrot', count: 6, interval: 1.3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 8, interval: 0.7, startDelay: 2 },
        ],
      },
      // 14 — everything the empire has left, before the throne.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 10, interval: 0.6, startDelay: 0 },
          { enemyType: 'carrotninja', count: 5, interval: 1.4, startDelay: 2 },
          { enemyType: 'brocco', count: 3, interval: 2.5, startDelay: 4 },
          { enemyType: 'potatoslicer', count: 4, interval: 2, startDelay: 6 },
          { enemyType: 'drycarrot', count: 4, interval: 1.8, startDelay: 8 },
        ],
      },
      // 15 — EMPEROR PEPRO. He floats out alone: the decree seals your builds
      // and his Mini Bell court refreshes from the sandy cloud itself.
      { groups: [{ enemyType: 'emperorpepro', count: 1, interval: 1.0, startDelay: 0 }] },
    ],
  },
  {
    // Chapter 3 · Episode VI — Fiery Lava. Past the temple the road drops into
    // the caldera: a single vast lava pool ringed by black volcanic rock, the
    // volcano itself smoking on the horizon. The trail is a chain of stepping
    // rocks across the molten surface — a long serpentine (~2480px) so the
    // late Caulifire waves stay under fire for their whole crossing.
    id: 21,
    chapter: 3,
    episode: 'VI',
    name: 'Fiery Lava',
    subtitle: 'The Caldera Crossing',
    background: 'bg-fierylava',
    // The crossing is a chain of stepping stones laid over the lava, not a road.
    trackStyle: 'rocks',
    path: [
      { x: 0, y: 160 },
      { x: 300, y: 160 },
      { x: 300, y: 420 },
      { x: 120, y: 420 },
      { x: 120, y: 620 },
      { x: 600, y: 620 },
      { x: 600, y: 300 },
      { x: 880, y: 300 },
      { x: 880, y: 600 },
      { x: 1040, y: 600 },
    ],
    // 17 rock outcrops hugging the trail (~56px off), spaced evenly along the
    // whole crossing so no stretch of the serpentine is uncovered.
    plots: [
      { id: 0, pos: { x: 52, y: 216 } },
      { id: 1, pos: { x: 182, y: 104 } },
      { id: 2, pos: { x: 356, y: 160 } },
      { id: 3, pos: { x: 244, y: 290 } },
      { id: 4, pos: { x: 356, y: 420 } },
      { id: 5, pos: { x: 196, y: 476 } },
      { id: 6, pos: { x: 64, y: 576 } },
      { id: 7, pos: { x: 198, y: 676 } },
      { id: 8, pos: { x: 380, y: 676 } },
      { id: 9, pos: { x: 510, y: 564 } },
      { id: 10, pos: { x: 656, y: 490 } },
      { id: 11, pos: { x: 544, y: 360 } },
      { id: 12, pos: { x: 652, y: 244 } },
      { id: 13, pos: { x: 782, y: 356 } },
      { id: 14, pos: { x: 936, y: 300 } },
      { id: 15, pos: { x: 936, y: 482 } },
      { id: 16, pos: { x: 880, y: 656 } },
    ],
    startCash: 4800,
    startLives: 5,
    coinReward: 90,
    waves: [
      // 1 — the veggie vanguard picks its way across the rocks.
      { groups: [{ enemyType: 'carrotcrusher', count: 6, interval: 1.0, startDelay: 0 }] },
      // 2 — Ninjas flank the crushers.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 7, interval: 0.9, startDelay: 0 },
          { enemyType: 'carrotninja', count: 2, interval: 2.5, startDelay: 3 },
        ],
      },
      // 3 — Broccos bring the fire-rate hex to the caldera.
      {
        groups: [
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 5, interval: 0.9, startDelay: 3 },
        ],
      },
      // 4 — Slicers under a dry escort.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'drycarrot', count: 2, interval: 2, startDelay: 2 },
        ],
      },
      // 5 — a Ninja rush.
      {
        groups: [
          { enemyType: 'carrotninja', count: 6, interval: 0.9, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 4 },
        ],
      },
      // 6 — jam and swarm: Dry Carrots stun while the crushers pour through.
      {
        groups: [
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 8, interval: 0.8, startDelay: 3 },
        ],
      },
      // 7 — the heavy line.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 2.5, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
        ],
      },
      // 8 — a full Ninja company with hexers behind.
      {
        groups: [
          { enemyType: 'carrotninja', count: 8, interval: 0.8, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 4 },
        ],
      },
      // 9 — the last wave before the rock cracks open.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 3 },
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 6 },
        ],
      },
      // 10 — CAULIFIRE debuts: two charred hulks haul themselves out of the lava.
      {
        groups: [
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 0 },
          { enemyType: 'carrotninja', count: 4, interval: 1.2, startDelay: 4 },
        ],
      },
      // 11 — the burning wall thickens.
      {
        groups: [
          { enemyType: 'caulifire', count: 3, interval: 3.5, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 3 },
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 7 },
        ],
      },
      // 12 — finale surge: the caldera empties itself at the trail.
      {
        groups: [
          { enemyType: 'caulifire', count: 5, interval: 3, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 4 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 8 },
          { enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 10 },
        ],
      },
    ],
  },
  {
    // Chapter 3 · Episode VII — The Volcanic Shores. The black-sand shoreline
    // at the volcano's foot, small lava pools steaming across the flats. Four
    // full-height sweeps (~2580px, the chapter's longest run) give the towers
    // the time they need once the Esparagus Mage starts blinking them out.
    id: 22,
    chapter: 3,
    episode: 'VII',
    name: 'The Volcanic Shores',
    subtitle: 'The Smoking Sands',
    background: 'bg-volcanicshores',
    path: [
      { x: 0, y: 600 },
      { x: 200, y: 600 },
      { x: 200, y: 220 },
      { x: 480, y: 220 },
      { x: 480, y: 560 },
      { x: 760, y: 560 },
      { x: 760, y: 180 },
      { x: 1000, y: 180 },
      { x: 1000, y: 620 },
      { x: 1040, y: 620 },
    ],
    // 17 pockets of cooled basalt between the lava pools, hugging the sweeps
    // so a stunned tower's neighbours still cover its lane.
    plots: [
      { id: 0, pos: { x: 52, y: 656 } },
      { id: 1, pos: { x: 256, y: 600 } },
      { id: 2, pos: { x: 144, y: 470 } },
      { id: 3, pos: { x: 256, y: 340 } },
      { id: 4, pos: { x: 200, y: 164 } },
      { id: 5, pos: { x: 382, y: 164 } },
      { id: 6, pos: { x: 536, y: 220 } },
      { id: 7, pos: { x: 424, y: 350 } },
      { id: 8, pos: { x: 536, y: 480 } },
      { id: 9, pos: { x: 636, y: 616 } },
      { id: 10, pos: { x: 816, y: 560 } },
      { id: 11, pos: { x: 704, y: 430 } },
      { id: 12, pos: { x: 816, y: 300 } },
      { id: 13, pos: { x: 760, y: 124 } },
      { id: 14, pos: { x: 942, y: 124 } },
      { id: 15, pos: { x: 944, y: 414 } },
      { id: 16, pos: { x: 1000, y: 676 } },
    ],
    startCash: 5000,
    startLives: 5,
    coinReward: 95,
    waves: [
      // 1 — Ninjas scout the black sand.
      { groups: [{ enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 0 }] },
      // 2 — the crusher tide with hexers behind.
      {
        groups: [
          { enemyType: 'carrotcrusher', count: 8, interval: 0.85, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 4 },
        ],
      },
      // 3 — Slicers lead a Ninja escort.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 4, interval: 1.2, startDelay: 3 },
        ],
      },
      // 4 — the shore's stun pressure opens up.
      {
        groups: [
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 4 },
        ],
      },
      // 5 — Caulifire returns from the caldera, now a regular of the line.
      {
        groups: [
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 4 },
        ],
      },
      // 6 — a heavy Slicer push.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 2.5, startDelay: 0 },
          { enemyType: 'carrotninja', count: 5, interval: 1.0, startDelay: 3 },
        ],
      },
      // 7 — charred hulks under a jamming escort.
      {
        groups: [
          { enemyType: 'caulifire', count: 3, interval: 3.5, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 4 },
        ],
      },
      // 8 — the combined arms wave, and the last quiet one.
      {
        groups: [
          { enemyType: 'brocco', count: 4, interval: 2.5, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 3 },
          { enemyType: 'carrotninja', count: 5, interval: 1.0, startDelay: 7 },
        ],
      },
      // 9 — THE ESPARAGUS MAGE debuts: one wizard walks the shore and two of
      // your towers go dark at a time.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 1, interval: 4, startDelay: 0 },
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 4 },
        ],
      },
      // 10 — a pair of mages: four towers can be sealed at once.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 2, interval: 5, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 4 },
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 8 },
        ],
      },
      // 11 — mages behind a burning wall.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 2, interval: 5, startDelay: 0 },
          { enemyType: 'caulifire', count: 3, interval: 3.5, startDelay: 3 },
          { enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 8 },
        ],
      },
      // 12 — finale surge: three mages keep the guns dark while the shore's
      // heaviest column walks through.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 3, interval: 4.5, startDelay: 0 },
          { enemyType: 'caulifire', count: 4, interval: 3, startDelay: 4 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 9 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 12 },
        ],
      },
    ],
  },
  {
    // Chapter 3 · Episode VIII — The Toxisisity. A radioactive dump: barrels
    // stacked in leaking piles, green sludge pooling between them. The roster
    // is deliberately THIN — no Carrot Crusher, no Potato Slicer, no Brocco —
    // so the dump reads as a different kind of fight from the castle waves:
    // fewer enemy shapes, each of them nastier, with the Ocraa vikings only
    // wading out of the sludge once the last four waves start.
    id: 23,
    chapter: 3,
    episode: 'VIII',
    name: 'The Toxisisity',
    subtitle: 'The Barrel Yard',
    background: 'bg-toxisisity',
    // Threaded through the gaps in the dump: the route never crosses a barrel
    // pile or a sludge puddle. Only the entry stub clips the perimeter fence
    // on the left edge, which is where the trail has to come in from.
    path: [
      { x: 0, y: 495 },
      { x: 360, y: 495 },
      { x: 360, y: 275 },
      { x: 570, y: 275 },
      { x: 570, y: 495 },
      { x: 780, y: 495 },
      { x: 780, y: 275 },
      { x: 990, y: 275 },
      { x: 990, y: 495 },
      { x: 1040, y: 495 },
    ],
    // 18 patches of bare ground, none of them on a barrel pile or a puddle,
    // and every one 50-65px off the trail centreline (the minimum is 44). The
    // three 210px-wide pockets each carry two columns, so both walls of every
    // sweep are covered.
    plots: [
      { id: 0, pos: { x: 415, y: 325 } },
      { id: 1, pos: { x: 415, y: 445 } },
      { id: 2, pos: { x: 515, y: 325 } },
      { id: 3, pos: { x: 515, y: 445 } },
      { id: 4, pos: { x: 625, y: 325 } },
      { id: 5, pos: { x: 625, y: 445 } },
      { id: 6, pos: { x: 725, y: 325 } },
      { id: 7, pos: { x: 725, y: 445 } },
      { id: 8, pos: { x: 835, y: 325 } },
      { id: 9, pos: { x: 835, y: 445 } },
      { id: 10, pos: { x: 935, y: 325 } },
      { id: 11, pos: { x: 935, y: 445 } },
      { id: 12, pos: { x: 340, y: 220 } },
      { id: 13, pos: { x: 560, y: 215 } },
      { id: 14, pos: { x: 980, y: 215 } },
      { id: 15, pos: { x: 1040, y: 330 } },
      { id: 16, pos: { x: 1040, y: 430 } },
      { id: 17, pos: { x: 340, y: 560 } },
    ],
    startCash: 5000,
    startLives: 5,
    coinReward: 100,
    waves: [
      // 1 — Ninjas pick their way through the barrels.
      { groups: [{ enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 0 }] },
      // 2 — the Ninja tide, first husks trailing it.
      {
        groups: [
          { enemyType: 'carrotninja', count: 8, interval: 0.85, startDelay: 0 },
          { enemyType: 'drycarrot', count: 2, interval: 3, startDelay: 4 },
        ],
      },
      // 3 — a pure stun wave: three husks, three tower blackouts.
      { groups: [{ enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 0 }] },
      // 4 — speed and stun together.
      {
        groups: [
          { enemyType: 'carrotninja', count: 6, interval: 0.9, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 3 },
        ],
      },
      // 5 — Caulifire wades in from the sludge.
      {
        groups: [
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 0 },
          { enemyType: 'carrotninja', count: 5, interval: 1.0, startDelay: 4 },
        ],
      },
      // 6 — stun pressure behind a burning wall.
      {
        groups: [
          { enemyType: 'drycarrot', count: 4, interval: 2, startDelay: 0 },
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 4 },
        ],
      },
      // 7 — the heaviest pre-mage push.
      {
        groups: [
          { enemyType: 'caulifire', count: 3, interval: 3.5, startDelay: 0 },
          { enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 4 },
        ],
      },
      // 8 — a mage joins the yard: two towers dark at a stroke.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 1, interval: 4, startDelay: 0 },
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 4 },
        ],
      },
      // 9 — two mages over a stun wave. The last wave before the vikings.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 2, interval: 5, startDelay: 0 },
          { enemyType: 'drycarrot', count: 4, interval: 2, startDelay: 4 },
        ],
      },
      // 10 — THE OCRAA debuts: helmeted okra vikings march out of the barrels.
      {
        groups: [
          { enemyType: 'ocraa', count: 3, interval: 2.5, startDelay: 0 },
          { enemyType: 'carrotninja', count: 5, interval: 1.0, startDelay: 4 },
        ],
      },
      // 11 — a viking column behind the charred wall.
      {
        groups: [
          { enemyType: 'ocraa', count: 4, interval: 2.2, startDelay: 0 },
          { enemyType: 'caulifire', count: 3, interval: 3.5, startDelay: 4 },
        ],
      },
      // 12 — vikings walking under mage cover.
      {
        groups: [
          { enemyType: 'ocraa', count: 5, interval: 2.0, startDelay: 0 },
          { enemyType: 'esparagusmage', count: 2, interval: 5, startDelay: 3 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 8 },
        ],
      },
      // 13 — finale: the whole yard empties at once.
      {
        groups: [
          { enemyType: 'ocraa', count: 6, interval: 1.8, startDelay: 0 },
          { enemyType: 'caulifire', count: 4, interval: 3, startDelay: 4 },
          { enemyType: 'esparagusmage', count: 3, interval: 4.5, startDelay: 8 },
          { enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 12 },
        ],
      },
    ],
  },
  {
    // Chapter 3 · Episode IX — Hambi's Dojo. A red-and-yellow temple over a
    // stone floor stamped with the Hambi Tower crest, a chinatown gate across
    // the front and Sensei Hambi watching from the steps. Five full-height
    // sweeps (~2780px, the longest route in the campaign) and the chapter's
    // fullest roster — every veggie the campaign has trained you against, plus
    // the Bamboo Crew, who take your BUILD away rather than a tower.
    id: 24,
    chapter: 3,
    episode: 'IX',
    name: "Hambi's Dojo",
    subtitle: 'The Training Grounds',
    background: 'bg-hambisdojo',
    // The route hugs the open flagstones and touches nothing painted into the
    // scene: it stays below the temple steps and Sensei Hambi, clear of the
    // corner punching bags and the paifang gate, and wraps AROUND the floor
    // crest on three sides (left at x=360, over the top at y=360, right at
    // x=900) rather than walking across it.
    path: [
      { x: 0, y: 300 },
      { x: 180, y: 300 },
      { x: 180, y: 620 },
      { x: 360, y: 620 },
      { x: 360, y: 360 },
      { x: 900, y: 360 },
      { x: 900, y: 620 },
      { x: 1040, y: 620 },
    ],
    // 18 mats on clear stone — none on the crest, the temple steps, the gate
    // or a corner punching bag.
    plots: [
      { id: 0, pos: { x: 120, y: 400 } },
      { id: 1, pos: { x: 120, y: 520 } },
      { id: 2, pos: { x: 270, y: 400 } },
      { id: 3, pos: { x: 270, y: 520 } },
      { id: 4, pos: { x: 420, y: 420 } },
      { id: 5, pos: { x: 420, y: 540 } },
      { id: 6, pos: { x: 300, y: 300 } },
      { id: 7, pos: { x: 460, y: 300 } },
      { id: 8, pos: { x: 540, y: 440 } },
      { id: 9, pos: { x: 740, y: 440 } },
      { id: 10, pos: { x: 820, y: 300 } },
      { id: 11, pos: { x: 940, y: 300 } },
      { id: 12, pos: { x: 840, y: 440 } },
      { id: 13, pos: { x: 960, y: 420 } },
      { id: 14, pos: { x: 960, y: 540 } },
      { id: 15, pos: { x: 1040, y: 340 } },
      { id: 16, pos: { x: 1040, y: 480 } },
      { id: 17, pos: { x: 180, y: 680 } },
    ],
    startCash: 5200,
    startLives: 5,
    coinReward: 105,
    waves: [
      // 1 — the dojo's first sparring partners.
      { groups: [{ enemyType: 'carrotcrusher', count: 6, interval: 1.0, startDelay: 0 }] },
      // 2 — Ninjas lead the crushers in.
      {
        groups: [
          { enemyType: 'carrotninja', count: 7, interval: 0.9, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 4, interval: 1.0, startDelay: 4 },
        ],
      },
      // 3 — hexers behind a crusher tide.
      {
        groups: [
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 0.9, startDelay: 3 },
        ],
      },
      // 4 — the Slicer wall arrives.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 2, interval: 3, startDelay: 0 },
          { enemyType: 'carrotninja', count: 5, interval: 1.1, startDelay: 3 },
        ],
      },
      // 5 — stun and hex at once.
      {
        groups: [
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 0 },
          { enemyType: 'brocco', count: 2, interval: 3, startDelay: 4 },
        ],
      },
      // 6 — Caulifire fronts the crusher tide.
      {
        groups: [
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 8, interval: 0.85, startDelay: 4 },
        ],
      },
      // 7 — Slicers under stun cover.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 2.5, startDelay: 0 },
          { enemyType: 'drycarrot', count: 3, interval: 2, startDelay: 4 },
        ],
      },
      // 8 — the Ocraa vikings follow the campaign in from the dump.
      {
        groups: [
          { enemyType: 'ocraa', count: 3, interval: 2.5, startDelay: 0 },
          { enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 4 },
        ],
      },
      // 9 — THE BAMBOO CREW debuts: one trio, and the build menu starts
      // going dark for 3s at a time.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 1, interval: 4, startDelay: 0 },
          { enemyType: 'caulifire', count: 2, interval: 4, startDelay: 4 },
        ],
      },
      // 10 — two crews: the seal is up nearly as often as it is down.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 2, interval: 5, startDelay: 0 },
          { enemyType: 'ocraa', count: 3, interval: 2.5, startDelay: 4 },
        ],
      },
      // 11 — towers dark AND the build sealed, together.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 2, interval: 5, startDelay: 0 },
          { enemyType: 'bamboocrew', count: 2, interval: 5, startDelay: 4 },
          { enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 8 },
        ],
      },
      // 12 — crews escorting the Slicer wall.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 3, interval: 4.5, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 4 },
        ],
      },
      // 13 — a breather from the seal, paid for in raw tonnage.
      {
        groups: [
          { enemyType: 'caulifire', count: 4, interval: 3, startDelay: 0 },
          { enemyType: 'ocraa', count: 4, interval: 2.2, startDelay: 4 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 8 },
        ],
      },
      // 14 — the full lockdown: build sealed, towers hexed, husks stunning.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 3, interval: 4.5, startDelay: 0 },
          { enemyType: 'esparagusmage', count: 2, interval: 5, startDelay: 4 },
          { enemyType: 'drycarrot', count: 4, interval: 2, startDelay: 8 },
        ],
      },
      // 15 — finale: the dojo's whole class, in one column.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 4, interval: 4, startDelay: 0 },
          { enemyType: 'caulifire', count: 4, interval: 3, startDelay: 4 },
          { enemyType: 'esparagusmage', count: 3, interval: 4.5, startDelay: 8 },
          { enemyType: 'ocraa', count: 5, interval: 2.0, startDelay: 12 },
          { enemyType: 'potatoslicer', count: 3, interval: 3, startDelay: 16 },
        ],
      },
    ],
  },
  {
    id: 25,
    chapter: 3,
    episode: 'X',
    name: 'The Ember and Flame',
    subtitle: 'The Ember Cave',
    background: 'bg-embercave',
    // The longest route in the campaign (~3440px, against a 1850px floor). The
    // horde pours out of the cave mouth at the left edge, crosses the carved
    // floor in front of it, then has to walk five switchbacks over the open
    // stone before it reaches the exit. Every leg stays clear of the painted
    // cliff on the left and the vine-trees down the right margin.
    path: [
      { x: 0, y: 280 },
      { x: 300, y: 280 },
      { x: 300, y: 140 },
      { x: 430, y: 140 },
      { x: 430, y: 640 },
      { x: 560, y: 640 },
      { x: 560, y: 140 },
      { x: 690, y: 140 },
      { x: 690, y: 640 },
      { x: 820, y: 640 },
      { x: 820, y: 140 },
      { x: 950, y: 140 },
      { x: 950, y: 400 },
      { x: 1040, y: 400 },
    ],
    // 24 plots — the most of any level. Twenty stand in the five 130px-wide
    // corridors between the switchback legs, so each one covers TWO passes of
    // the route (65px of clearance to the leg on either side), and four make a
    // last-stand column by the exit. Every pair is >=96px apart and none comes
    // within 44px of the trail.
    plots: [
      { id: 0, pos: { x: 365, y: 210 } },
      { id: 1, pos: { x: 365, y: 340 } },
      { id: 2, pos: { x: 365, y: 470 } },
      { id: 3, pos: { x: 365, y: 600 } },
      { id: 4, pos: { x: 495, y: 140 } },
      { id: 5, pos: { x: 495, y: 270 } },
      { id: 6, pos: { x: 495, y: 400 } },
      { id: 7, pos: { x: 495, y: 530 } },
      { id: 8, pos: { x: 625, y: 210 } },
      { id: 9, pos: { x: 625, y: 340 } },
      { id: 10, pos: { x: 625, y: 470 } },
      { id: 11, pos: { x: 625, y: 600 } },
      { id: 12, pos: { x: 755, y: 140 } },
      { id: 13, pos: { x: 755, y: 270 } },
      { id: 14, pos: { x: 755, y: 400 } },
      { id: 15, pos: { x: 755, y: 530 } },
      { id: 16, pos: { x: 885, y: 210 } },
      { id: 17, pos: { x: 885, y: 340 } },
      { id: 18, pos: { x: 885, y: 470 } },
      { id: 19, pos: { x: 885, y: 600 } },
      { id: 20, pos: { x: 1010, y: 200 } },
      { id: 21, pos: { x: 1010, y: 330 } },
      { id: 22, pos: { x: 1010, y: 470 } },
      { id: 23, pos: { x: 1010, y: 590 } },
    ],
    startCash: 6000,
    startLives: 5,
    coinReward: 150,
    // The campaign finale earns the confetti.
    winConfetti: true,
    waves: [
      // 1 — the Crushers test the cavern floor.
      { groups: [{ enemyType: 'carrotcrusher', count: 8, interval: 0.9, startDelay: 0 }] },
      // 2 — Ninjas run with them.
      {
        groups: [
          { enemyType: 'carrotninja', count: 7, interval: 0.9, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 5, interval: 1.0, startDelay: 4 },
        ],
      },
      // 3 — Broccos hex from behind a Crusher tide.
      {
        groups: [
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 7, interval: 0.9, startDelay: 3 },
        ],
      },
      // 4 — the Slicer wall, screened by Ninjas.
      {
        groups: [
          { enemyType: 'potatoslicer', count: 3, interval: 2.5, startDelay: 0 },
          { enemyType: 'carrotninja', count: 6, interval: 1.0, startDelay: 3 },
        ],
      },
      // 5 — AN EMPEROR PEPRO rides ahead of the king, Mini Bell in tow.
      {
        groups: [
          { enemyType: 'peproherald', count: 1, interval: 1, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 6, interval: 1.0, startDelay: 6 },
        ],
      },
      // 6 — Dry Carrots blink towers out between Ninja runs.
      {
        groups: [
          { enemyType: 'drycarrot', count: 4, interval: 2, startDelay: 0 },
          { enemyType: 'carrotninja', count: 6, interval: 0.9, startDelay: 4 },
        ],
      },
      // 7 — the Ocraa vikings, hexed along by Broccos.
      {
        groups: [
          { enemyType: 'ocraa', count: 4, interval: 2.2, startDelay: 0 },
          { enemyType: 'brocco', count: 3, interval: 3, startDelay: 4 },
        ],
      },
      // 8 — Caulifire fronts the Crusher tide.
      {
        groups: [
          { enemyType: 'caulifire', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'carrotcrusher', count: 8, interval: 0.85, startDelay: 4 },
        ],
      },
      // 9 — the Mages arrive: two towers go dark at a stroke.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 2, interval: 4, startDelay: 0 },
          { enemyType: 'carrotninja', count: 7, interval: 0.9, startDelay: 4 },
        ],
      },
      // 10 — A SECOND EMPEROR PEPRO, behind the Slicer wall.
      {
        groups: [
          { enemyType: 'peproherald', count: 1, interval: 1, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 5 },
        ],
      },
      // 11 — the Bamboo Crew debuts here: the build seals while towers blink.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 2, interval: 4, startDelay: 0 },
          { enemyType: 'drycarrot', count: 4, interval: 2, startDelay: 4 },
        ],
      },
      // 12 — two walls at once.
      {
        groups: [
          { enemyType: 'caulifire', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'ocraa', count: 5, interval: 2.0, startDelay: 4 },
        ],
      },
      // 13 — a double Mage hex over a Ninja rush.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 3, interval: 4, startDelay: 0 },
          { enemyType: 'carrotninja', count: 8, interval: 0.8, startDelay: 4 },
        ],
      },
      // 14 — crews escorting the Slicers.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 2, interval: 5, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 4, interval: 2.5, startDelay: 4 },
        ],
      },
      // 15 — THE LAST EMPEROR PEPRO, with Caulifire cover.
      {
        groups: [
          { enemyType: 'peproherald', count: 1, interval: 1, startDelay: 0 },
          { enemyType: 'caulifire', count: 3, interval: 3, startDelay: 5 },
        ],
      },
      // 16 — crew, mage and viking together.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 2, interval: 5, startDelay: 0 },
          { enemyType: 'esparagusmage', count: 2, interval: 5, startDelay: 4 },
          { enemyType: 'ocraa', count: 5, interval: 2.0, startDelay: 8 },
        ],
      },
      // 17 — the whole veggie line, mixed.
      {
        groups: [
          { enemyType: 'caulifire', count: 3, interval: 3, startDelay: 0 },
          { enemyType: 'drycarrot', count: 5, interval: 1.8, startDelay: 3 },
          { enemyType: 'carrotcrusher', count: 8, interval: 0.85, startDelay: 7 },
        ],
      },
      // 18 — Slicers and Caulifire under a crew lockdown.
      {
        groups: [
          { enemyType: 'bamboocrew', count: 3, interval: 4, startDelay: 0 },
          { enemyType: 'potatoslicer', count: 5, interval: 2.2, startDelay: 4 },
          { enemyType: 'caulifire', count: 3, interval: 3, startDelay: 8 },
        ],
      },
      // 19 — the full roster, back to back, before the king walks out.
      {
        groups: [
          { enemyType: 'esparagusmage', count: 3, interval: 4, startDelay: 0 },
          { enemyType: 'bamboocrew', count: 3, interval: 4, startDelay: 3 },
          { enemyType: 'ocraa', count: 5, interval: 2.0, startDelay: 7 },
          { enemyType: 'brocco', count: 4, interval: 2.5, startDelay: 11 },
          { enemyType: 'carrotninja', count: 8, interval: 0.8, startDelay: 15 },
        ],
      },
      // 20 — KING EMBER. From the moment he steps out of the cave every tower
      // has 3 HP and a bar over it, and a fireball takes one off a random tower
      // every 5 seconds. His honour guard keeps you from simply massing on him.
      {
        groups: [
          { enemyType: 'kingember', count: 1, interval: 1, startDelay: 0 },
          { enemyType: 'caulifire', count: 4, interval: 3.5, startDelay: 8 },
          { enemyType: 'bamboocrew', count: 3, interval: 5, startDelay: 16 },
          { enemyType: 'potatoslicer', count: 5, interval: 2.5, startDelay: 26 },
        ],
      },
    ],
  },
];

/** Highest level id in the campaign (clamps the unlock cursor). */
export const LAST_LEVEL_ID = LEVELS[LEVELS.length - 1].id;

/** Campaign chapter a level belongs to (defaults to 1). */
export function chapterOf(level: LevelDef): number {
  return level.chapter ?? 1;
}

/** Enemy types that count as campaign bosses (finale waves, boss music, ★ tag). */
export const BOSS_ENEMY_TYPES = [
  'boss',
  'nullnugget',
  'kingnuggetron',
  'emperorpepro',
  'kingember',
  // The Moon's boss (The Hub, level 105 — see config/hubLevels.ts).
  'chiplysdad',
  // The Fungi Forest's boss (Mushy Mayhem, level 115).
  'themushy',
  'kingshroometron',
] as const;

/** Does a level end on a boss wave (a Nugget finale — Golden or Null)? */
export function hasBoss(level: LevelDef): boolean {
  return level.waves.some((w) =>
    w.groups.some((g) => (BOSS_ENEMY_TYPES as readonly string[]).includes(g.enemyType)),
  );
}

/** The boss enemy a level's finale fields, or null for boss-less levels. */
export function bossTypeOf(level: LevelDef): (typeof BOSS_ENEMY_TYPES)[number] | null {
  for (const t of BOSS_ENEMY_TYPES) {
    if (level.waves.some((w) => w.groups.some((g) => g.enemyType === t))) return t;
  }
  return null;
}

/** Distinct chapter numbers present in the campaign, ascending. */
export const CHAPTERS: number[] = [...new Set(LEVELS.map(chapterOf))].sort((a, b) => a - b);

/** Levels belonging to a chapter, in campaign order. */
export function levelsInChapter(chapter: number): LevelDef[] {
  return LEVELS.filter((l) => chapterOf(l) === chapter);
}

/**
 * Background-music tracks, keyed by the mp3 filename (minus extension) in
 * assets/music/. Each entry covers a run of consecutive episodes within a
 * chapter, so neighbouring episodes share a loop. `episodes` are 1-based
 * positions within the chapter (Episode I = 1). The Chapter-2 finale track
 * spans episodes 8–10 to cover episodes not yet built out.
 */
const MUSIC_TRACKS: { chapter: number; episodes: number[]; key: string }[] = [
  { chapter: 1, episodes: [1, 2], key: 'C1-E1_E2' },
  { chapter: 1, episodes: [3, 4], key: 'C1-E3_E4' },
  { chapter: 1, episodes: [5], key: 'C1-E5' },
  { chapter: 2, episodes: [1, 2], key: 'C2-E1_E2' },
  { chapter: 2, episodes: [3, 4], key: 'C2-E3_E4' },
  { chapter: 2, episodes: [5], key: 'C2-E5' },
  { chapter: 2, episodes: [6, 7], key: 'C2-E6_E7' },
  { chapter: 2, episodes: [8, 9, 10], key: 'C2-E8_E9_E10' },
  // Chapter 3 (2026-10-06).
  { chapter: 3, episodes: [1, 2], key: 'C3-E1_E2' },
  { chapter: 3, episodes: [3, 4], key: 'C3-E3_E4' },
  { chapter: 3, episodes: [5], key: 'C3-E5' },
  { chapter: 3, episodes: [6, 7], key: 'C3-E6_E7' },
  { chapter: 3, episodes: [8, 9], key: 'C3-E8_E9' },
  { chapter: 3, episodes: [10], key: 'C3-E10' },
];

/**
 * The Moon's tracks (2026-10-06), keyed by 1-based position in `HUB_LEVELS`
 * (Hambi Bar = 1 … Through Space and Time = 10). The boss track `HC-L5` plays
 * on BOTH boss fights — The Space Station (5) and the finale (10) — and the
 * file the player named `HC-L5-6` covers 6–7 (the player's call, 2026-10-06).
 */
const HUB_MUSIC: { positions: number[]; key: string }[] = [
  { positions: [1, 2], key: 'HC-L1_L2' },
  { positions: [3, 4], key: 'HC-L3_L4' },
  { positions: [5, 10], key: 'HC-L5' },
  { positions: [6, 7], key: 'HC-L6_L7' },
  { positions: [8, 9], key: 'HC-L8_L9' },
];

/** One loop for The Dark Shadows (the Origin Event's levels, 201–202). */
export const ORIGIN_MUSIC_TRACK = 'Origin-D1_D2';

/** The Hub's menu theme: HubScene and the chapter menus opened from it. */
export const HUB_MENU_TRACK = 'hub-menu';

/**
 * The music-track key for a level, chosen by its chapter and 1-based episode
 * position within that chapter. Falls back to the chapter's last track (then
 * the very first track) for any episode outside the mapped ranges, so a newly
 * added episode always gets a real loop rather than silence. MusicService
 * no-ops if the key was never loaded.
 */
export function musicKeyFor(level: LevelDef): string {
  // The Hub and the Origin Event first: their levels carry `chapter: 1` (their
  // OWN chapter numbering) but are not campaign episodes, so the lookup below
  // would hand them Chapter 1's last loop. Arenas fall through to that too.
  if (isOriginLevelId(level.id)) return ORIGIN_MUSIC_TRACK;
  const hubPos = HUB_LEVELS.findIndex((l) => l.id === level.id) + 1;
  if (hubPos > 0) return HUB_MUSIC.find((t) => t.positions.includes(hubPos))?.key ?? '';
  const chapter = chapterOf(level);
  const episode = levelsInChapter(chapter).findIndex((l) => l.id === level.id) + 1;
  const inChapter = MUSIC_TRACKS.filter((t) => t.chapter === chapter);
  // A chapter with no tracks yet plays no music — return an
  // empty key, which MusicService no-ops on (rather than borrowing Chapter 1's
  // loop). Music is added per chapter later.
  if (inChapter.length === 0) return '';
  const match = inChapter.find((t) => t.episodes.includes(episode));
  return (match ?? inChapter[inChapter.length - 1]).key;
}

/**
 * First level id of Chapter 2 — the point its content "starts". The shop's
 * second page (Chapter-2 towers) opens as soon as this level is unlocked,
 * i.e. the moment Chapter 2 becomes playable, not when it is cleared.
 * Falls back to the last level id if the campaign has no Chapter 2.
 */
export const CH2_START_LEVEL_ID = levelsInChapter(2)[0]?.id ?? LAST_LEVEL_ID;

/**
 * Last level id of Chapter 2 — its finale. Clearing it is the Evolution gate.
 * Falls back to the last level id if the campaign has no Chapter 2.
 */
export const CH2_FINALE_LEVEL_ID =
  levelsInChapter(2)[levelsInChapter(2).length - 1]?.id ?? LAST_LEVEL_ID;

/**
 * True once ALL of Chapter 2 is completed. Winning the finale (≥1★) implies
 * every earlier episode was cleared (each unlock gate needs ≥1★), and the
 * unlock cursor moving past the finale implies the same — either signal counts,
 * so debug-unlocked saves and genuinely played saves both pass.
 */
export function isChapter2Cleared(p: {
  stars(id: number): number;
  unlockedThrough(): number;
}): boolean {
  return p.stars(CH2_FINALE_LEVEL_ID) >= 1 || p.unlockedThrough() > CH2_FINALE_LEVEL_ID;
}

/**
 * Level ids whose *next* level begins a new chapter — i.e. chapter finales.
 * Advancing from these uses the relaxed win-only unlock gate (see ProgressService),
 * so beating a chapter's last episode opens the next chapter on any win.
 */
export const WIN_GATE_LEVEL_IDS: number[] = LEVELS.filter((l, i) => {
  const next = LEVELS[i + 1];
  return next != null && chapterOf(next) !== chapterOf(l);
}).map((l) => l.id);

/**
 * Rank exp a level pays on its first win. A level may set `expReward`
 * outright (the hub's do); otherwise it follows the chapter: Chapter 2 pays
 * 100, Chapter 3 pays 150, Chapter 1 pays nothing. Shown as "+N EXP" beside
 * the coin reward on every picker row, and paid by GameScene on a first win.
 */
export function expRewardFor(level: LevelDef): number {
  if (level.expReward !== undefined) return level.expReward;
  const ch = chapterOf(level);
  return ch === 2 ? 100 : ch === 3 ? 150 : 0;
}
